import { useEffect, useMemo, useState, type ReactNode } from "react";
import {
  Button,
  Input,
  Textarea,
  Field,
  Radio,
  RadioGroup,
  Select,
  makeStyles,
  shorthands,
  tokens,
  Spinner,
} from "@fluentui/react-components";
import {
  Building24Regular,
  PersonAvailable24Regular,
  ShieldTask24Regular,
  Home24Regular,
  Money24Regular,
  VehicleCar24Regular,
  People24Regular,
  Gift24Regular,
  ChatBubblesQuestion24Regular,
  DocumentRibbon24Regular,
  Briefcase24Regular,
  Shield24Regular,
  Heart24Regular,
  CoinMultiple24Regular,
  GlobeShield24Regular,
} from "@fluentui/react-icons";
import { apiUrl } from "../utils/api";
import { FluentTelefonInput } from "./forms/widgets/TelefonEingabe";

const BRAND = {
  fontFamilyPrimary: "'Barlow', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
  fontFamilyAccent: "'Playfair Display', Georgia, 'Times New Roman', serif",
  colors: {
    pageBg: "#361936",
    primaryPurple: "#4F266B",
    accent: "#F8AA00",
    accentHover: "#F8AA00",
    cardBg: "#fbf9fc",
    border: "#4F266B",
    textOnDark: "#B4AFB7",
    muted: "#B4AFB7",
    pillarOrange: "#E66A2C",
    pillarBlue: "#3D8BD9",
    pillarGreen: "#2E7D4F",
    pillarBlack: "#1F1B24",
  },
};

const useStyles = makeStyles({
  page: {
    minHeight: "100vh",
    backgroundColor: BRAND.colors.pageBg,
    backgroundImage:
      "radial-gradient(120% 120% at 85% 15%, #4F266B 0%, #3f1f57 28%, #361936 62%, #361936 100%)",
    color: tokens.colorNeutralForegroundInverted,
    fontFamily: BRAND.fontFamilyPrimary,
  },
  hero: {
    maxWidth: "1100px",
    marginLeft: "auto",
    marginRight: "auto",
    ...shorthands.padding("28px", "16px", "20px", "16px"),
    "@media (min-width: 768px)": {
      ...shorthands.padding("48px", "24px", "20px", "24px"),
    },
  },
  title: {
    marginTop: 0,
    marginBottom: "12px",
    color: BRAND.colors.accent,
    fontSize: "clamp(2rem, 8vw, 4.5rem)",
    lineHeight: 1.05,
    fontWeight: 400,
    fontFamily: BRAND.fontFamilyAccent,
  },
  subtitle: {
    marginTop: 0,
    marginBottom: "8px",
    color: BRAND.colors.textOnDark,
    fontSize: "clamp(0.95rem, 3.2vw, 1.15rem)",
  },
  pillarsCard: {
    maxWidth: "1100px",
    marginLeft: "auto",
    marginRight: "auto",
    backgroundColor: BRAND.colors.primaryPurple,
    backgroundImage:
      "linear-gradient(135deg, #4F266B 0%, #3a1c52 100%)",
    ...shorthands.borderRadius(tokens.borderRadiusLarge),
    ...shorthands.padding("18px"),
    "@media (min-width: 768px)": {
      ...shorthands.padding("24px"),
    },
  },
  pillarsHeading: {
    marginTop: 0,
    marginBottom: "16px",
    color: BRAND.colors.accent,
    fontFamily: BRAND.fontFamilyAccent,
    fontWeight: 400,
    fontSize: "clamp(1.1rem, 3.6vw, 1.6rem)",
    lineHeight: 1.2,
  },
  pillarsRow: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "12px",
    "@media (min-width: 768px)": {
      gridTemplateColumns: "repeat(4, 1fr)",
      gap: "16px",
    },
  },
  pillar: {
    color: "#fff",
    ...shorthands.borderRadius("8px"),
    ...shorthands.padding("16px", "14px"),
    minHeight: "150px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    fontWeight: 600,
    fontSize: "0.95rem",
    gap: "10px",
    "@media (min-width: 768px)": {
      minHeight: "190px",
    },
  },
  pillarNumber: {
    width: "28px",
    height: "28px",
    ...shorthands.borderRadius("50%"),
    backgroundColor: "#fff",
    color: BRAND.colors.pageBg,
    fontWeight: 700,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "0.9rem",
  },
  formWrap: {
    maxWidth: "1100px",
    marginLeft: "auto",
    marginRight: "auto",
    ...shorthands.padding("16px", "16px", "28px", "16px"),
    "@media (min-width: 768px)": {
      ...shorthands.padding("24px", "24px", "40px", "24px"),
    },
  },
  formCard: {
    backgroundColor: BRAND.colors.cardBg,
    color: tokens.colorNeutralForeground1,
    ...shorthands.borderRadius(tokens.borderRadiusLarge),
    ...shorthands.border("1px", "solid", BRAND.colors.border),
    boxShadow: tokens.shadow16,
    ...shorthands.padding("16px"),
    "@media (min-width: 768px)": {
      ...shorthands.padding("28px"),
    },
  },
  formIntroTitle: {
    marginTop: 0,
    marginBottom: "8px",
    fontSize: "clamp(1.2rem, 4vw, 1.8rem)",
    color: BRAND.colors.pageBg,
    fontFamily: BRAND.fontFamilyAccent,
    fontWeight: 400,
  },
  formHint: {
    marginTop: 0,
    marginBottom: "20px",
    color: BRAND.colors.muted,
  },
  sectionWrap: {
    marginTop: "32px",
  },
  sectionHeader: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    color: BRAND.colors.primaryPurple,
    fontFamily: BRAND.fontFamilyAccent,
    fontWeight: 400,
    fontSize: "clamp(1.1rem, 3.4vw, 1.45rem)",
    marginTop: 0,
    marginBottom: "10px",
    paddingBottom: "8px",
    ...shorthands.borderBottom("1px", "solid", "#E3E0E7"),
  },
  sectionIcon: {
    color: BRAND.colors.primaryPurple,
    fontSize: "24px",
    flexShrink: 0,
  },
  sectionHint: {
    marginTop: 0,
    marginBottom: "14px",
    color: BRAND.colors.muted,
    fontSize: "0.95rem",
  },
  fields: {
    display: "grid",
    gap: "14px",
    gridTemplateColumns: "1fr",
  },
  fields3: {
    display: "grid",
    gap: "14px",
    gridTemplateColumns: "1fr",
  },
  fullSpan: {
    gridColumn: "1 / -1",
  },
  fullWidth: {
    width: "100%",
  },
  questionBlock: {
    paddingTop: "14px",
    paddingBottom: "14px",
    ...shorthands.borderBottom("1px", "solid", "#ECE7F0"),
  },
  questionText: {
    fontWeight: 600,
    color: BRAND.colors.pageBg,
    fontSize: "1rem",
    marginBottom: "4px",
    lineHeight: 1.35,
  },
  questionHint: {
    color: BRAND.colors.muted,
    fontSize: "0.85rem",
    marginBottom: "8px",
  },
  questionBody: {
    display: "grid",
    gap: "10px",
  },
  reveal: {
    marginTop: "8px",
    paddingLeft: "12px",
    ...shorthands.borderLeft("3px", "solid", BRAND.colors.accent),
    display: "grid",
    gap: "10px",
  },
  riskQuestion: {
    display: "grid",
    gap: "6px",
    paddingTop: "10px",
    paddingBottom: "10px",
    ...shorthands.borderBottom("1px", "dashed", "#E3E0E7"),
  },
  riskLabelRow: {
    display: "flex",
    alignItems: "flex-start",
    gap: "8px",
  },
  riskQuestionIcon: {
    color: BRAND.colors.primaryPurple,
    flexShrink: 0,
    marginTop: "2px",
  },
  subSection: {
    marginTop: "16px",
    backgroundColor: "#F7F4FA",
    ...shorthands.borderRadius("8px"),
    ...shorthands.padding("14px"),
    ...shorthands.border("1px", "solid", "#E3E0E7"),
  },
  subSectionTitle: {
    marginTop: 0,
    marginBottom: "8px",
    fontWeight: 600,
    color: BRAND.colors.primaryPurple,
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  rowAddButton: {
    marginTop: "8px",
  },
  actions: {
    marginTop: "32px",
    display: "flex",
    justifyContent: "flex-end",
  },
  submitButton: {
    width: "100%",
    minHeight: "48px",
    backgroundColor: BRAND.colors.accent,
    color: BRAND.colors.pageBg,
    border: `1px solid ${BRAND.colors.accent}`,
    fontWeight: 700,
    fontSize: "1.05rem",
    ":hover": {
      backgroundColor: BRAND.colors.accentHover,
      border: `1px solid ${BRAND.colors.accentHover}`,
      color: BRAND.colors.pageBg,
    },
    "@media (min-width: 640px)": {
      width: "auto",
      minWidth: "240px",
    },
  },
  stateCard: {
    maxWidth: "680px",
    margin: "48px auto",
    backgroundColor: BRAND.colors.cardBg,
    color: BRAND.colors.pageBg,
    ...shorthands.border("1px", "solid", BRAND.colors.border),
    ...shorthands.borderRadius(tokens.borderRadiusLarge),
    ...shorthands.padding("20px"),
    boxShadow: tokens.shadow8,
    textAlign: "center",
  },
  twoCol: {
    display: "grid",
    gap: "8px",
    gridTemplateColumns: "1fr",
    "@media (min-width: 768px)": {
      gridTemplateColumns: "1fr 1fr",
      gap: "12px",
    },
  },
});

type YesNo = "" | "ja" | "nein";

type RiskQuestion = {
  key: string;
  label: string;
  detailsLabel?: string;
};

const RISK_QUESTIONS: RiskQuestion[] = [
  { key: "risiko_taetigkeit", label: "Aus Ihrer Tätigkeit?" },
  { key: "risiko_fremde_grundstuecke", label: "Aufgrund von Arbeiten auf fremden Grundstücken?" },
  { key: "risiko_herstellung_vertrieb", label: "Aufgrund der Herstellung oder des Vertriebes von Produkten?" },
  { key: "risiko_lohnfertigung", label: "Aufgrund von Lohnfertigungen durch Sie?" },
  { key: "risiko_schadenersatz_betrieb", label: "Sind Sie Schadenersatzrisiken aus dem Betrieb und Produkten ausgesetzt?" },
  { key: "risiko_ware_weiterverarbeitet", label: "Wird Ware von Ihnen weiterverarbeitet, ein- und/oder ausgebaut?" },
  { key: "risiko_ware_ausland", label: "Beziehen Sie Ware/Produkte aus dem Ausland und verarbeiten/verkaufen diese weiter?" },
  { key: "risiko_eigenes_label", label: "Werden Waren/Produkte mit Ihrem Namen gelabelt?" },
  { key: "risiko_beratung_planung", label: "Sind Sie beratend oder planend tätig?" },
  {
    key: "risiko_branche_verkehr",
    label: "Arbeiten Sie für die Auto-/Fahrzeugteile-/Luftfahrt-/Schifffahrtsindustrie und/oder Bahn?",
    detailsLabel: "Wenn ja: Branchen, Anteil",
  },
  { key: "risiko_arbeit_an_fahrzeugen", label: "Arbeiten Sie an Fahrzeugen?" },
  { key: "risiko_umweltgefaehrdung", label: "Sind Sie Schadenersatzrisiken aus Umweltgefährdung ausgesetzt?" },
  {
    key: "risiko_anlagen",
    label: "Umweltrisiken / Anlagen (Öltank, Notstromaggregat, Öl-/Benzin-/Fettabscheider, Maschineninhalte, brennbare Gase ab 3 t)?",
    detailsLabel: "Wenn ja: was und wo",
  },
  { key: "risiko_organhaftung", label: "Unterliegt die Geschäftsleitung der Organhaftung?" },
  { key: "risiko_nutztiere", label: "Gibt es Nutztiere?" },
  {
    key: "risiko_abfaelle",
    label: "Welche Abfälle fallen an und müssen gesondert gelagert / entsorgt werden?",
    detailsLabel: "Bitte beschreiben",
  },
  { key: "risiko_obhut", label: "Nehmen Sie Sachen in Obhut?" },
  { key: "risiko_rueckruf", label: "Ist der Rückruf von Produkten denkbar?" },
  { key: "risiko_produkteigenschaften", label: "Sagen Sie Produkteigenschaften zu?" },
  { key: "risiko_eigenschaeden_mitarbeiter", label: "Durch mögliche Fehler von Mitarbeitern (Eigenschäden)?" },
  { key: "risiko_organfehler", label: "Durch mögliche Fehler von Organen in Unternehmensführung/Lenkung?" },
  { key: "risiko_boeswillig", label: "Durch böswillige Handlungen von Vertrauenspersonen / Dritten?" },
  { key: "risiko_montageobjekte", label: "Aus eigenen oder fremden Montageobjekten in Planung/Ausführung?" },
  { key: "risiko_planungsleistungen", label: "Aufgrund von Planungsleistungen für Dritte?" },
  { key: "risiko_beratungsleistungen", label: "Aufgrund von Beratungsleistungen für Dritte?" },
  { key: "risiko_bauleistungen", label: "Aufgrund von Bauleistungen für Ihr Unternehmen?" },
  { key: "risiko_montageleistungen", label: "Aufgrund von erbrachten Montageleistungen?" },
  {
    key: "risiko_vertrag_kunden",
    label: "Aufgrund vertraglicher Vereinbarungen mit Kunden (AGBs, Auftragsbestätigung, sonstige)?",
    detailsLabel: "Wenn ja: bitte beschreiben (Anhänge fordern wir später an)",
  },
  { key: "risiko_verlaengerte_gewaehrleistung", label: "Verlängerung der Verjährung der gesetzlichen Gewährleistung?" },
  { key: "risiko_abbedingung_pruefpflicht", label: "Abbedingung der kaufmännischen Prüf- und Rügepflicht?" },
  {
    key: "risiko_umweltrisiken_tanks",
    label: "Aufgrund von Umweltrisiken (Tanks etc.)?",
    detailsLabel: "Wenn ja: bitte beschreiben",
  },
  { key: "risiko_betriebsunterbrechung_eigen", label: "Betriebsunterbrechung bei Ihnen im Unternehmen?" },
  { key: "risiko_betriebsunterbrechung_fremd", label: "Betriebsunterbrechung durch Schäden bei Zulieferern oder Abnehmern?" },
  { key: "risiko_seuchengefahr", label: "Betriebsschließung durch Behörden wegen Seuchengefahr?" },
  { key: "risiko_mietausfall", label: "Mietausfall aus vermieteten Gebäuden/Räumlichkeiten?" },
  { key: "risiko_patentinhaber", label: "Sind Sie Patentinhaber?" },
  { key: "risiko_cyberangriffe", label: "Aufgrund von Cyberangriffen?" },
  { key: "risiko_datenschutzverletzungen", label: "Aufgrund von Datenschutzverletzungen?" },
  { key: "risiko_softwarelizenzen_daten", label: "Aufgrund Verlust von Softwarelizenzen und/oder Daten?" },
  { key: "risiko_buergschaften", label: "Aufgrund erteilter Sicherheiten/Bürgschaften?" },
  { key: "risiko_rechtsauseinandersetzungen", label: "Aufgrund rechtlicher Auseinandersetzungen?" },
  { key: "risiko_urheberrechte", label: "Rechtsstreitigkeiten aus Urheberrechtsverletzungen?" },
  { key: "risiko_patentrechte", label: "Rechtsstreitigkeiten aus Patentrechtsverletzungen?" },
  { key: "risiko_dienstwerkvertraege", label: "Rechtsstreitigkeiten aus Dienstleistung-/Werkverträgen?" },
  { key: "risiko_zahlungsausfaelle", label: "Aufgrund von Zahlungsausfällen Ihrer Kunden?" },
  { key: "risiko_mahnwesen", label: "Haben Sie ein funktionierendes Mahnwesen?" },
  { key: "risiko_kunden_zulieferer_insolvenz", label: "Aufgrund Kunden- und/oder Zulieferer-Insolvenz?" },
  { key: "risiko_dienstreisen", label: "Aufgrund Geschäfts- und Dienstreisen?" },
];

type Person = {
  nachname: string;
  vorname: string;
  geburtsdatum: string;
  kontaktdaten: string;
  anteile?: string;
  firma?: string;
  rolle?: string;
};

type Standort = {
  ort: string;
  plz: string;
  strasse: string;
  hausnummer: string;
  eigentum: "" | "eigentum" | "miete" | "pacht" | "leasing";
  eigentuemer: string;
  flaecheGesamt: string;
  flaecheSelbst: string;
  untervermietung: YesNo;
  grundstuecksbestandteile: YesNo;
  heizung: string;
  trafo: YesNo;
  istFirmensitz: YesNo;
  istPostanschrift: YesNo;
  einfriedungJa: YesNo;
  einfriedung: string;
  einfriedungWo: string;
  einbruchmeldeanlageJa: YesNo;
  einbruchmeldeanlage: string;
  einbruchmeldeanlageWo: string;
  brandmeldeanlageJa: YesNo;
  brandmeldeanlage: string;
  brandmeldeanlageWo: string;
  rwaJa: YesNo;
  rwa: string;
  rwaWo: string;
  sprinklerJa: YesNo;
  sprinkler: string;
  sprinklerWo: string;
  stromabschaltungJa: YesNo;
  stromabschaltung: string;
  stromabschaltungWo: string;
  videoueberwachungJa: YesNo;
  videoueberwachung: string;
  videoueberwachungWo: string;
  notstromJa: YesNo;
  notstrom: string;
  notstromWo: string;
  sonstigeSicherungJa: YesNo;
  sonstigeSicherung: string;
  notfallplan: YesNo;
  wiederaufnahmedauer: string;
  weiterePraeventionJa: YesNo;
  weiterePraevention: string;
};

const emptyPerson = (): Person => ({
  nachname: "",
  vorname: "",
  geburtsdatum: "",
  kontaktdaten: "",
  anteile: "",
  firma: "",
  rolle: "",
});

const emptyStandort = (): Standort => ({
  ort: "",
  plz: "",
  strasse: "",
  hausnummer: "",
  eigentum: "",
  eigentuemer: "",
  flaecheGesamt: "",
  flaecheSelbst: "",
  untervermietung: "",
  grundstuecksbestandteile: "",
  heizung: "",
  trafo: "",
  istFirmensitz: "",
  istPostanschrift: "",
  einfriedungJa: "",
  einfriedung: "",
  einfriedungWo: "",
  einbruchmeldeanlageJa: "",
  einbruchmeldeanlage: "",
  einbruchmeldeanlageWo: "",
  brandmeldeanlageJa: "",
  brandmeldeanlage: "",
  brandmeldeanlageWo: "",
  rwaJa: "",
  rwa: "",
  rwaWo: "",
  sprinklerJa: "",
  sprinkler: "",
  sprinklerWo: "",
  stromabschaltungJa: "",
  stromabschaltung: "",
  stromabschaltungWo: "",
  videoueberwachungJa: "",
  videoueberwachung: "",
  videoueberwachungWo: "",
  notstromJa: "",
  notstrom: "",
  notstromWo: "",
  sonstigeSicherungJa: "",
  sonstigeSicherung: "",
  notfallplan: "",
  wiederaufnahmedauer: "",
  weiterePraeventionJa: "",
  weiterePraevention: "",
});

/**
 * Vermögenswerte des Betriebs. Jede Kategorie ist eine eigene Position der
 * Sach-/Inhaltsversicherung — eigene Versicherungssumme oder eigener Vertrag.
 *
 * Mietereinbauten stehen hier bewusst NICHT: Sie sind Bestandteil des
 * Gebäudes und werden je Einheit im Immobilien-Fragebogen erfasst
 * (`risikoImmobilieStep/unitFollowUpSpec.ts` — vorhanden? / was? / Wert /
 * in wessen Besitz? / mitversichern?). Dort steht auch die Zurechnungsfrage
 * Vermieter ↔ Mieter, die die Kategorie überhaupt erst entscheidbar macht.
 * Zweimal gefragt hieße zwei Werte, die auseinanderlaufen.
 */
const ASSET_CATEGORIES: Array<{ key: string; label: string }> = [
  { key: "kaufm_betriebseinrichtung", label: "Kaufmännische Betriebseinrichtung" },
  { key: "elektronik", label: "Technische Betriebseinrichtung – Elektronik" },
  { key: "maschinen_stationaer", label: "Maschinen stationär" },
  { key: "maschinen_mobil", label: "Maschinen mobil" },
  { key: "selbstfahrende_arbeitsmaschinen", label: "Selbstfahrende Arbeitsmaschinen" },
  { key: "waren_vorraete", label: "Waren / Vorräte" },
  { key: "kundeneigentum", label: "Kundeneigentum" },
  { key: "auf_baustellen", label: "Auf Baustellen" },
  { key: "wertsachen", label: "Wertsachen (Bargeld, Urkunden, Edelmetalle, …)" },
  { key: "sonstiges_assets", label: "Sonstiges" },
  { key: "immobilien", label: "Immobilien" },
  { key: "photovoltaik", label: "Photovoltaikanlagen" },
  { key: "ladestationen", label: "Ladestationen" },
  { key: "grundstuecke", label: "Grundstücke" },
];

const BENEFIT_CATEGORIES: Array<{ key: string; label: string }> = [
  { key: "sachbezug", label: "Sachbezug" },
  { key: "bike_leasing", label: "Bike-Leasing" },
  { key: "anwesenheitspraemie", label: "Anwesenheitsprämie" },
  { key: "weihnachts_urlaubsgeld", label: "Weihnachts- und Urlaubsgeld" },
  { key: "aufmerksamkeiten", label: "Aufmerksamkeiten" },
  { key: "vermoegenswirksame_leistungen", label: "Vermögenswirksame Leistungen" },
  { key: "entgeltumwandlung", label: "Entgeltumwandlung" },
  { key: "arbeitgeberfinanzierte_versorgungen", label: "Arbeitgeberfinanzierte Versorgungen" },
  { key: "betriebliche_kv_tagegeld_bu", label: "Betriebliche Krankenversicherung, Tagegeld, BU" },
  { key: "zeitwertkonten", label: "Zeitwertkonten" },
  { key: "sonstiges_benefit", label: "Sonstiges" },
];

// ─────────────────────────────────────────────────────────────────────
// Module-scoped helpers
//
// IMPORTANT: QuestionBlock / RevealBox MUST live at module scope. If they
// are defined inside RisikoanalyseFormView, every parent re-render creates
// a fresh component reference, React unmounts/remounts the whole subtree,
// and any input that was being typed loses focus — and the page scrolls
// back to the top.
// ─────────────────────────────────────────────────────────────────────

const useQuestionBlockStyles = makeStyles({
  questionBlock: {
    paddingTop: "14px",
    paddingBottom: "14px",
    ...shorthands.borderBottom("1px", "solid", "#ECE7F0"),
  },
  questionText: {
    fontWeight: 600,
    color: "#361936",
    fontSize: "1rem",
    marginBottom: "4px",
    lineHeight: 1.35,
  },
  questionHint: {
    color: "#B4AFB7",
    fontSize: "0.85rem",
    marginBottom: "8px",
  },
  questionBody: {
    display: "grid",
    gap: "10px",
  },
  questionBodyTwoCol: {
    display: "grid",
    gap: "10px",
    "@media (min-width: 720px)": {
      gridTemplateColumns: "1fr 1fr",
      columnGap: "20px",
    },
  },
  reveal: {
    marginTop: "8px",
    paddingLeft: "12px",
    ...shorthands.borderLeft("3px", "solid", "#F8AA00"),
    display: "grid",
    gap: "10px",
  },
});

const QuestionBlock = ({
  question,
  hint,
  twoCol = false,
  children,
}: {
  question: string;
  hint?: string;
  twoCol?: boolean;
  children: ReactNode;
}) => {
  const s = useQuestionBlockStyles();
  return (
    <div className={s.questionBlock}>
      <div className={s.questionText}>{question}</div>
      {hint && <div className={s.questionHint}>{hint}</div>}
      <div className={twoCol ? s.questionBodyTwoCol : s.questionBody}>{children}</div>
    </div>
  );
};

const RevealBox = ({ children }: { children: ReactNode }) => {
  const s = useQuestionBlockStyles();
  return <div className={s.reveal}>{children}</div>;
};

const ifYes = (value: string, node: ReactNode) =>
  value === "ja" ? <RevealBox>{node}</RevealBox> : null;

const ifNo = (value: string, node: ReactNode) =>
  value === "nein" ? <RevealBox>{node}</RevealBox> : null;

type RisikoanalyseFormViewProps = {
  token: string | undefined;
  chrome?: "public" | "embedded";
  onSubmitSuccess?: () => void;
};

const RisikoanalyseFormView = ({
  token,
  chrome = "public",
  onSubmitSuccess,
}: RisikoanalyseFormViewProps) => {
  const styles = useStyles();
  const [loading, setLoading] = useState(true);
  const [valid, setValid] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitOk, setSubmitOk] = useState(false);

  // ── Page 1: Customer info & founding info
  const [name, setName] = useState("");
  const [strasse, setStrasse] = useState("");
  const [plz, setPlz] = useState("");
  const [stadt, setStadt] = useState("");
  const [land, setLand] = useState("");
  const [telefon, setTelefon] = useState("");
  const [fax, setFax] = useState("");
  const [mail, setMail] = useState("");
  const [webseite, setWebseite] = useState("");
  const [kontaktdatenKorrekt, setKontaktdatenKorrekt] = useState<YesNo>("");

  const [gruendungsjahr, setGruendungsjahr] = useState("");
  const [gruender, setGruender] = useState("");
  const [rechtsform, setRechtsform] = useState("");
  const [handelsregisternummer, setHandelsregisternummer] = useState("");
  const [register, setRegister] = useState("");
  const [steuernummer, setSteuernummer] = useState("");
  const [umsatzsteuernummer, setUmsatzsteuernummer] = useState("");
  const [finanzamt, setFinanzamt] = useState("");
  const [vorsteuerabzug, setVorsteuerabzug] = useState<YesNo>("");
  const [geschaeftsjahrKalender, setGeschaeftsjahrKalender] = useState<YesNo>("");
  const [geschaeftsjahrAbweichung, setGeschaeftsjahrAbweichung] = useState("");
  const [beteiligungenJaNein, setBeteiligungenJaNein] = useState<YesNo>("");
  const [beteiligungen, setBeteiligungen] = useState("");
  const [niederlassungenJaNein, setNiederlassungenJaNein] = useState<YesNo>("");
  const [niederlassungen, setNiederlassungen] = useState("");
  const [rechtsvorgaengerJaNein, setRechtsvorgaengerJaNein] = useState<YesNo>("");
  const [rechtsvorgaenger, setRechtsvorgaenger] = useState("");

  // ── Page 2: Shareholders & responsibilities
  const [geschaeftsfuehrer, setGeschaeftsfuehrer] = useState<Person[]>([
    emptyPerson(),
    emptyPerson(),
  ]);
  const [gesellschafter, setGesellschafter] = useState<Person[]>([
    emptyPerson(),
    emptyPerson(),
  ]);
  const [weitereBeteiligungenJaNein, setWeitereBeteiligungenJaNein] = useState<YesNo>("");
  const [weitereBeteiligungenGesellschafter, setWeitereBeteiligungenGesellschafter] =
    useState("");
  const [spezielleVersorgungenJaNein, setSpezielleVersorgungenJaNein] = useState<YesNo>("");
  const [spezielleVersorgungen, setSpezielleVersorgungen] = useState("");
  const [veraenderungenGesellschaftJaNein, setVeraenderungenGesellschaftJaNein] = useState<YesNo>("");
  const [veraenderungenGesellschaft, setVeraenderungenGesellschaft] = useState("");
  const [zustaendigkeiten, setZustaendigkeiten] = useState<Person[]>([
    emptyPerson(),
    emptyPerson(),
  ]);
  const [qualitaetsmanagementJaNein, setQualitaetsmanagementJaNein] = useState<YesNo>("");
  const [qualitaetsmanagement, setQualitaetsmanagement] = useState("");
  const [qmVerantwortlich, setQmVerantwortlich] = useState("");

  // ── Page 3: Activity
  const [taetigkeit, setTaetigkeit] = useState("");
  const [taetigkeitWeiteres, setTaetigkeitWeiteres] = useState("");
  const [b2b, setB2b] = useState<YesNo>("");
  const [b2bDetails, setB2bDetails] = useState("");
  const [b2c, setB2c] = useState<YesNo>("");
  const [b2cDetails, setB2cDetails] = useState("");
  const [sonstigeKunden, setSonstigeKunden] = useState<YesNo>("");
  const [sonstigeKundenDetails, setSonstigeKundenDetails] = useState("");
  const [abhaengigkeitenKunden, setAbhaengigkeitenKunden] = useState<YesNo>("");
  const [vertriebsschwerpunkt, setVertriebsschwerpunkt] = useState("");
  const [kundeninformation, setKundeninformation] = useState("");
  const [hauptlieferanten, setHauptlieferanten] = useState("");
  const [abhaengigkeitenLieferanten, setAbhaengigkeitenLieferanten] = useState<YesNo>("");

  // ── Pages 4-5: Risks
  const [riskAnswers, setRiskAnswers] = useState<Record<string, string>>({});
  const [riskNotes, setRiskNotes] = useState<Record<string, string>>({});
  const [risikenAllgemein, setRisikenAllgemein] = useState("");

  // ── Page 6: Locations
  const [standorte, setStandorte] = useState<Standort[]>([emptyStandort()]);
  const [investitionenStandorteGeplant, setInvestitionenStandorteGeplant] =
    useState<YesNo>("");
  const [investitionenStandorteDetails, setInvestitionenStandorteDetails] = useState("");

  // ── Page 7: Assets
  const [assets, setAssets] = useState<
    Record<string, { vorhanden: YesNo; was: string; wo: string; wert: string }>
  >({});
  const [fahrzeugeEigeneJa, setFahrzeugeEigeneJa] = useState<YesNo>("");
  const [fahrzeugeEigene, setFahrzeugeEigene] = useState({ was: "", anzahl: "" });
  const [fahrzeugeFremde, setFahrzeugeFremde] = useState<YesNo>("");
  const [fahrzeugeFremdeDetails, setFahrzeugeFremdeDetails] = useState({ was: "", anzahl: "" });
  const [fahrzeugeSonstigeJa, setFahrzeugeSonstigeJa] = useState<YesNo>("");
  const [fahrzeugeSonstige, setFahrzeugeSonstige] = useState({ was: "", anzahl: "" });
  const [vermoegenInvestitionenGeplant, setVermoegenInvestitionenGeplant] = useState<YesNo>("");
  const [vermoegenInvestitionenDetails, setVermoegenInvestitionenDetails] = useState("");

  // ── Page 8: Transport & KPIs
  const [versendenWaren, setVersendenWaren] = useState<YesNo>("");
  const [versendenWie, setVersendenWie] = useState("");
  const [versendenFahrzeuge, setVersendenFahrzeuge] = useState("");
  const [versendenGefahr, setVersendenGefahr] = useState("");
  const [versendenWert, setVersendenWert] = useState("");
  const [erhaltenBetriebsmittel, setErhaltenBetriebsmittel] = useState("");
  const [erhaltenWie, setErhaltenWie] = useState("");
  const [erhaltenGefahr, setErhaltenGefahr] = useState("");
  const [erhaltenWert, setErhaltenWert] = useState("");
  const [zwischenlagerungen, setZwischenlagerungen] = useState("");
  const [transportFuerDritte, setTransportFuerDritte] = useState<YesNo>("");
  const [lagerhalter, setLagerhalter] = useState<YesNo>("");
  const [messen, setMessen] = useState<YesNo>("");

  const [umsatz, setUmsatz] = useState("");
  const [wareneinsatz, setWareneinsatz] = useState("");
  const [rohertrag, setRohertrag] = useState("");
  const [umsatzschwankungen, setUmsatzschwankungen] = useState<YesNo>("");
  const [umsatzschwankungenWarum, setUmsatzschwankungenWarum] = useState("");
  const [sondereffekte, setSondereffekte] = useState<YesNo>("");
  const [sondereffekteWarum, setSondereffekteWarum] = useState("");
  const [innenumsaetze, setInnenumsaetze] = useState<YesNo>("");
  const [innenumsaetzeDetails, setInnenumsaetzeDetails] = useState("");
  const [umsatzDeutschland, setUmsatzDeutschland] = useState("");
  const [umsatzEuropa, setUmsatzEuropa] = useState("");
  const [umsatzWeltweit, setUmsatzWeltweit] = useState("");
  const [umsatzUsaKanada, setUmsatzUsaKanada] = useState("");
  const [brancheAutomotive, setBrancheAutomotive] = useState({ jaNein: "" as YesNo, wieviel: "", warum: "" });
  const [brancheSchifffahrt, setBrancheSchifffahrt] = useState({ jaNein: "" as YesNo, wieviel: "", warum: "" });
  const [brancheLuftfahrt, setBrancheLuftfahrt] = useState({ jaNein: "" as YesNo, wieviel: "", warum: "" });
  const [brancheSchienenverkehr, setBrancheSchienenverkehr] = useState({ jaNein: "" as YesNo, wieviel: "", warum: "" });
  const [rohertragSchwankungen, setRohertragSchwankungen] = useState({ jaNein: "" as YesNo, wieviel: "", warum: "" });
  const [wareneinsatzquote, setWareneinsatzquote] = useState("");
  const [eigenkapital, setEigenkapital] = useState("");
  const [kreditrahmen, setKreditrahmen] = useState("");
  const [darlehnVerbindlichkeiten, setDarlehnVerbindlichkeiten] = useState("");
  const [buergschaften, setBuergschaften] = useState("");
  const [liquiditaetKurz, setLiquiditaetKurz] = useState("");
  const [liquiditaetMittel, setLiquiditaetMittel] = useState("");
  const [liquiditaetLang, setLiquiditaetLang] = useState("");

  // ── Page 9: People
  const [mitarbeiterInsgesamt, setMitarbeiterInsgesamt] = useState("");
  const [vollzeitkraefte, setVollzeitkraefte] = useState("");
  const [monatsstunden, setMonatsstunden] = useState("");
  const [teilzeitkraefte, setTeilzeitkraefte] = useState("");
  const [minijobber, setMinijobber] = useState("");
  const [midijobber, setMidijobber] = useState("");
  const [gleichzeitig, setGleichzeitig] = useState("");
  const [bgGesamt, setBgGesamt] = useState("");
  const [bgBuero, setBgBuero] = useState("");
  const [bgGewerblich, setBgGewerblich] = useState("");
  const [bgAussendienst, setBgAussendienst] = useState("");
  const [bgAushilfen, setBgAushilfen] = useState("");
  const [bgGeschaeftsfuehrung, setBgGeschaeftsfuehrung] = useState("");
  const [leiharbeiter, setLeiharbeiter] = useState<YesNo>("");
  const [leiharbeiterAnzahl, setLeiharbeiterAnzahl] = useState("");
  const [leiharbeiterRegelmaessig, setLeiharbeiterRegelmaessig] = useState("");
  const [subunternehmer, setSubunternehmer] = useState<YesNo>("");
  const [subunternehmerAnzahl, setSubunternehmerAnzahl] = useState("");
  const [subunternehmerUmsatzanteil, setSubunternehmerUmsatzanteil] = useState("");
  const [freelancer, setFreelancer] = useState<YesNo>("");
  const [freelancerAnzahl, setFreelancerAnzahl] = useState("");
  const [freelancerAufwand, setFreelancerAufwand] = useState("");
  const [bereichBuero, setBereichBuero] = useState("");
  const [bereichProduktion, setBereichProduktion] = useState("");
  const [bereichPlanung, setBereichPlanung] = useState("");
  const [bereichFremdeGrundstuecke, setBereichFremdeGrundstuecke] = useState("");
  const [bereichMontage, setBereichMontage] = useState("");
  const [entsendung, setEntsendung] = useState<YesNo>("");
  const [entsendungWelche, setEntsendungWelche] = useState("");
  const [entsendungWohin, setEntsendungWohin] = useState("");
  const [entsendungDauer, setEntsendungDauer] = useState("");
  const [eigenerPkw, setEigenerPkw] = useState<YesNo>("");
  const [eigenerPkwTage, setEigenerPkwTage] = useState("");
  const [eigenerPkwKm, setEigenerPkwKm] = useState("");

  // ── Page 10: Benefits & sonstiges
  const [benefitsJaNein, setBenefitsJaNein] = useState<Record<string, string>>({});
  const [benefits, setBenefits] = useState<Record<string, string>>({});
  const [newsletter, setNewsletter] = useState<YesNo>("");
  const [newsletterDetails, setNewsletterDetails] = useState("");
  const [tarifvertragUnterliegt, setTarifvertragUnterliegt] = useState<YesNo>("");
  const [tarifvertragUnterliegtDetails, setTarifvertragUnterliegtDetails] = useState("");
  const [tarifvertragOrientiert, setTarifvertragOrientiert] = useState<YesNo>("");
  const [tarifvertragOrientiertDetails, setTarifvertragOrientiertDetails] = useState("");
  const [ihkHwk, setIhkHwk] = useState("");
  const [berufsverbaende, setBerufsverbaende] = useState<YesNo>("");
  const [berufsverbaendeDetails, setBerufsverbaendeDetails] = useState("");
  const [sonstigeMitgliedschaften, setSonstigeMitgliedschaften] = useState<YesNo>("");
  const [sonstigeMitgliedschaftenDetails, setSonstigeMitgliedschaftenDetails] = useState("");
  const [erwartung, setErwartung] = useState("");
  const [besonderheitenVertrag, setBesonderheitenVertrag] = useState("");

  // ── Page 11: Closing
  const [zufriedenheitMakler, setZufriedenheitMakler] = useState("");
  const [zusaetzlicheHinweise, setZusaetzlicheHinweise] = useState("");
  const [unterlagenOrganigramm, setUnterlagenOrganigramm] = useState(false);
  const [unterlagenBilanz, setUnterlagenBilanz] = useState(false);
  const [unterlagenAgb, setUnterlagenAgb] = useState(false);
  const [unterlagenGenehmigungen, setUnterlagenGenehmigungen] = useState("");
  const [unterlagenBrandschutz, setUnterlagenBrandschutz] = useState(false);
  const [unterlagenSonstige, setUnterlagenSonstige] = useState("");

  useEffect(() => {
    const checkToken = async () => {
      if (!token) {
        setValid(false);
        setLoading(false);
        return;
      }
      try {
        const res = await fetch(apiUrl(`public-form/${token}`));
        if (!res.ok) {
          setValid(false);
          return;
        }
        setValid(true);
        try {
          const data = await res.json();
          const c = data?.contact;
          if (c) {
            setName(c.displayName || c.lastname || "");
            if (c.address) {
              setStrasse(c.address.street ?? "");
              setStadt(c.address.city ?? "");
              setPlz(c.address.postalCode ?? "");
              setLand(c.address.country ?? "");
            }
            setTelefon(c.phone ?? "");
            setMail(c.email ?? "");
            setWebseite(c.website ?? "");
          }
        } catch {
          // Body wasn't JSON or didn't include contact — leave fields empty
        }
      } catch {
        setValid(false);
      } finally {
        setLoading(false);
      }
    };
    checkToken();
  }, [token]);

  const setRisk = (key: string, value: string) =>
    setRiskAnswers((prev) => ({ ...prev, [key]: value }));
  const setRiskNote = (key: string, value: string) =>
    setRiskNotes((prev) => ({ ...prev, [key]: value }));

  const updatePerson =
    (list: Person[], setter: (next: Person[]) => void) =>
    (idx: number, field: keyof Person, value: string) => {
      const next = list.map((p, i) => (i === idx ? { ...p, [field]: value } : p));
      setter(next);
    };

  const addPerson = (list: Person[], setter: (next: Person[]) => void) => () =>
    setter([...list, emptyPerson()]);

  const updateStandort = (idx: number, field: keyof Standort, value: string) => {
    setStandorte((prev) =>
      prev.map((s, i) => (i === idx ? { ...s, [field]: value as never } : s))
    );
  };

  const setAssetField = (
    key: string,
    field: "vorhanden" | "was" | "wo" | "wert",
    value: string
  ) =>
    setAssets((prev) => {
      const current = prev[key] ?? { vorhanden: "" as YesNo, was: "", wo: "", wert: "" };
      return {
        ...prev,
        [key]: { ...current, [field]: value },
      };
    });

  const payload = useMemo(
    () => ({
      kontakt: {
        name,
        strasse,
        plz,
        stadt,
        land,
        telefon,
        fax,
        mail,
        webseite,
        kontaktdatenKorrekt,
      },
      gruendung: {
        gruendungsjahr,
        gruender,
        rechtsform,
        handelsregisternummer,
        register,
        steuernummer,
        umsatzsteuernummer,
        finanzamt,
        vorsteuerabzug,
        geschaeftsjahrKalender,
        geschaeftsjahrAbweichung,
        beteiligungenJaNein,
        beteiligungen,
        niederlassungenJaNein,
        niederlassungen,
        rechtsvorgaengerJaNein,
        rechtsvorgaenger,
      },
      personal: {
        geschaeftsfuehrer,
        gesellschafter,
        weitereBeteiligungenJaNein,
        weitereBeteiligungenGesellschafter,
        spezielleVersorgungenJaNein,
        spezielleVersorgungen,
        veraenderungenGesellschaftJaNein,
        veraenderungenGesellschaft,
        zustaendigkeiten,
        qualitaetsmanagementJaNein,
        qualitaetsmanagement,
        qmVerantwortlich,
      },
      betriebscharakter: {
        taetigkeit,
        taetigkeitWeiteres,
        b2b,
        b2bDetails,
        b2c,
        b2cDetails,
        sonstigeKunden,
        sonstigeKundenDetails,
        abhaengigkeitenKunden,
        vertriebsschwerpunkt,
        kundeninformation,
        hauptlieferanten,
        abhaengigkeitenLieferanten,
      },
      risiken: {
        allgemein: risikenAllgemein,
        antworten: riskAnswers,
        details: riskNotes,
      },
      betriebsstaetten: {
        standorte,
        investitionenGeplant: investitionenStandorteGeplant,
        investitionenDetails: investitionenStandorteDetails,
      },
      vermoegen: {
        kategorien: assets,
        fahrzeugeEigeneJa,
        fahrzeugeEigene,
        fahrzeugeFremde,
        fahrzeugeFremdeDetails,
        fahrzeugeSonstigeJa,
        fahrzeugeSonstige,
        investitionenGeplant: vermoegenInvestitionenGeplant,
        investitionenDetails: vermoegenInvestitionenDetails,
      },
      transport: {
        versendenWaren,
        versendenWie,
        versendenFahrzeuge,
        versendenGefahr,
        versendenWert,
        erhaltenBetriebsmittel,
        erhaltenWie,
        erhaltenGefahr,
        erhaltenWert,
        zwischenlagerungen,
        transportFuerDritte,
        lagerhalter,
        messen,
      },
      kennzahlen: {
        umsatz,
        wareneinsatz,
        rohertrag,
        umsatzschwankungen,
        umsatzschwankungenWarum,
        sondereffekte,
        sondereffekteWarum,
        innenumsaetze,
        innenumsaetzeDetails,
        umsatzVerteilung: {
          deutschland: umsatzDeutschland,
          europa: umsatzEuropa,
          weltweit: umsatzWeltweit,
          usaKanada: umsatzUsaKanada,
        },
        branchen: {
          automotive: brancheAutomotive,
          schifffahrt: brancheSchifffahrt,
          luftfahrt: brancheLuftfahrt,
          schienenverkehr: brancheSchienenverkehr,
        },
        rohertragSchwankungen,
        wareneinsatzquote,
        eigenkapital,
        kreditrahmen,
        darlehnVerbindlichkeiten,
        buergschaften,
        liquiditaet: {
          kurz: liquiditaetKurz,
          mittel: liquiditaetMittel,
          lang: liquiditaetLang,
        },
      },
      menschen: {
        mitarbeiterInsgesamt,
        vollzeitkraefte,
        monatsstunden,
        teilzeitkraefte,
        minijobber,
        midijobber,
        gleichzeitig,
        bgGesamt,
        bgBuero,
        bgGewerblich,
        bgAussendienst,
        bgAushilfen,
        bgGeschaeftsfuehrung,
        leiharbeiter,
        leiharbeiterAnzahl,
        leiharbeiterRegelmaessig,
        subunternehmer,
        subunternehmerAnzahl,
        subunternehmerUmsatzanteil,
        freelancer,
        freelancerAnzahl,
        freelancerAufwand,
        bereiche: {
          buero: bereichBuero,
          produktion: bereichProduktion,
          planung: bereichPlanung,
          fremdeGrundstuecke: bereichFremdeGrundstuecke,
          montage: bereichMontage,
        },
        entsendung,
        entsendungWelche,
        entsendungWohin,
        entsendungDauer,
        eigenerPkw,
        eigenerPkwTage,
        eigenerPkwKm,
      },
      benefits,
      benefitsJaNein,
      sonstiges: {
        newsletter,
        newsletterDetails,
        tarifvertragUnterliegt,
        tarifvertragUnterliegtDetails,
        tarifvertragOrientiert,
        tarifvertragOrientiertDetails,
        ihkHwk,
        berufsverbaende,
        berufsverbaendeDetails,
        sonstigeMitgliedschaften,
        sonstigeMitgliedschaftenDetails,
        erwartung,
        besonderheitenVertrag,
      },
      abschluss: {
        zufriedenheitMakler,
        zusaetzlicheHinweise,
        unterlagen: {
          organigramm: unterlagenOrganigramm,
          bilanz: unterlagenBilanz,
          agb: unterlagenAgb,
          genehmigungen: unterlagenGenehmigungen,
          brandschutz: unterlagenBrandschutz,
          sonstige: unterlagenSonstige,
        },
      },
    }),
    [
      name, strasse, plz, stadt, land, telefon, fax, mail, webseite, kontaktdatenKorrekt,
      gruendungsjahr, gruender, rechtsform, handelsregisternummer, register, steuernummer, umsatzsteuernummer, finanzamt, vorsteuerabzug, geschaeftsjahrKalender, geschaeftsjahrAbweichung, beteiligungenJaNein, beteiligungen, niederlassungenJaNein, niederlassungen, rechtsvorgaengerJaNein, rechtsvorgaenger,
      geschaeftsfuehrer, gesellschafter, weitereBeteiligungenJaNein, weitereBeteiligungenGesellschafter, spezielleVersorgungenJaNein, spezielleVersorgungen, veraenderungenGesellschaftJaNein, veraenderungenGesellschaft, zustaendigkeiten, qualitaetsmanagementJaNein, qualitaetsmanagement, qmVerantwortlich,
      taetigkeit, taetigkeitWeiteres, b2b, b2bDetails, b2c, b2cDetails, sonstigeKunden, sonstigeKundenDetails, abhaengigkeitenKunden, vertriebsschwerpunkt, kundeninformation, hauptlieferanten, abhaengigkeitenLieferanten,
      risikenAllgemein, riskAnswers, riskNotes,
      standorte, investitionenStandorteGeplant, investitionenStandorteDetails,
      assets, fahrzeugeEigeneJa, fahrzeugeEigene, fahrzeugeFremde, fahrzeugeFremdeDetails, fahrzeugeSonstigeJa, fahrzeugeSonstige, vermoegenInvestitionenGeplant, vermoegenInvestitionenDetails,
      versendenWaren, versendenWie, versendenFahrzeuge, versendenGefahr, versendenWert, erhaltenBetriebsmittel, erhaltenWie, erhaltenGefahr, erhaltenWert, zwischenlagerungen, transportFuerDritte, lagerhalter, messen,
      umsatz, wareneinsatz, rohertrag, umsatzschwankungen, umsatzschwankungenWarum, sondereffekte, sondereffekteWarum, innenumsaetze, innenumsaetzeDetails,
      umsatzDeutschland, umsatzEuropa, umsatzWeltweit, umsatzUsaKanada,
      brancheAutomotive, brancheSchifffahrt, brancheLuftfahrt, brancheSchienenverkehr,
      rohertragSchwankungen, wareneinsatzquote, eigenkapital, kreditrahmen, darlehnVerbindlichkeiten, buergschaften,
      liquiditaetKurz, liquiditaetMittel, liquiditaetLang,
      mitarbeiterInsgesamt, vollzeitkraefte, monatsstunden, teilzeitkraefte, minijobber, midijobber, gleichzeitig,
      bgGesamt, bgBuero, bgGewerblich, bgAussendienst, bgAushilfen, bgGeschaeftsfuehrung,
      leiharbeiter, leiharbeiterAnzahl, leiharbeiterRegelmaessig, subunternehmer, subunternehmerAnzahl, subunternehmerUmsatzanteil, freelancer, freelancerAnzahl, freelancerAufwand,
      bereichBuero, bereichProduktion, bereichPlanung, bereichFremdeGrundstuecke, bereichMontage,
      entsendung, entsendungWelche, entsendungWohin, entsendungDauer, eigenerPkw, eigenerPkwTage, eigenerPkwKm,
      benefits, benefitsJaNein,
      newsletter, newsletterDetails, tarifvertragUnterliegt, tarifvertragUnterliegtDetails, tarifvertragOrientiert, tarifvertragOrientiertDetails, ihkHwk, berufsverbaende, berufsverbaendeDetails, sonstigeMitgliedschaften, sonstigeMitgliedschaftenDetails, erwartung, besonderheitenVertrag,
      zufriedenheitMakler, zusaetzlicheHinweise,
      unterlagenOrganigramm, unterlagenBilanz, unterlagenAgb, unterlagenGenehmigungen, unterlagenBrandschutz, unterlagenSonstige,
    ]
  );

  const handleSubmit = async () => {
    if (!token) return;
    setSubmitting(true);
    try {
      const res = await fetch(apiUrl(`public-form/${token}`), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        if (onSubmitSuccess) {
          onSubmitSuccess();
        } else {
          setSubmitOk(true);
        }
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className={chrome === "public" ? styles.page : undefined}>
        <div className={styles.stateCard}>
          <Spinner label="Formular wird geladen…" />
        </div>
      </div>
    );
  }

  if (!valid) {
    return (
      <div className={chrome === "public" ? styles.page : undefined}>
        <div className={styles.stateCard}>Der Link ist ungültig oder abgelaufen.</div>
      </div>
    );
  }

  if (submitOk) {
    return (
      <div className={chrome === "public" ? styles.page : undefined}>
        <div className={styles.stateCard}>Vielen Dank, Ihre Risikoanalyse wurde übermittelt.</div>
      </div>
    );
  }

  const renderRadioJaNein = (
    value: string,
    onChange: (v: YesNo) => void
  ) => (
    <RadioGroup
      value={value}
      onChange={(_, data) => onChange(data.value as YesNo)}
      layout="horizontal"
    >
      <Radio value="ja" label="Ja" />
      <Radio value="nein" label="Nein" />
    </RadioGroup>
  );

  return (
    <div className={chrome === "public" ? styles.page : undefined}>
      {chrome === "public" && (
        <div className={styles.hero}>
          <h1 className={styles.title}>Risikoanalyse</h1>
          <p className={styles.subtitle}>
            Zum Kennenlernen Ihres Unternehmens und zur Ersteinschätzung Ihrer Risiken und Ihres
            Bedarfs haben wir ein paar Fragen, bevor wir später noch in die Detailanalyse einsteigen.
          </p>
        </div>
      )}

      <div style={{ maxWidth: 1100, margin: "0 auto", padding: "0 16px 16px" }}>
        <div className={styles.pillarsCard}>
          <h2 className={styles.pillarsHeading}>
            Risiken – identifizieren, bewerten, reduzieren, absichern und optimieren (auch die Chancen daraus)
          </h2>
          <div className={styles.pillarsRow}>
            <div className={styles.pillar} style={{ backgroundColor: BRAND.colors.pillarOrange }}>
              <span className={styles.pillarNumber}>1</span>
              <Briefcase24Regular />
              <div>Verlust / Beschädigung / Zerstörung von Sachwerten</div>
            </div>
            <div className={styles.pillar} style={{ backgroundColor: BRAND.colors.pillarBlue }}>
              <span className={styles.pillarNumber}>2</span>
              <Shield24Regular />
              <div>Haftung / Recht</div>
            </div>
            <div className={styles.pillar} style={{ backgroundColor: BRAND.colors.pillarGreen }}>
              <span className={styles.pillarNumber}>3</span>
              <Heart24Regular />
              <div>Menschen im Unternehmen</div>
            </div>
            <div className={styles.pillar} style={{ backgroundColor: BRAND.colors.pillarBlack }}>
              <span className={styles.pillarNumber}>4</span>
              <CoinMultiple24Regular />
              <div>Liquidität / Ertragsausfall</div>
            </div>
          </div>
        </div>
      </div>

      <div className={styles.formWrap}>
        <div className={styles.formCard}>
          <h2 className={styles.formIntroTitle}>Kundenrisikoanalyse</h2>
          <p className={styles.formHint}>
            Bitte beantworten Sie die Fragen so vollständig wie möglich. Felder können leer
            bleiben, wenn sie auf Ihr Unternehmen nicht zutreffen. Ihre Angaben werden an uns
            übermittelt und vertraulich behandelt.
          </p>

          {/* ─────────── Section 1: Kontaktdaten ─────────── */}
          <div className={styles.sectionWrap}>
            <h3 className={styles.sectionHeader}>
              <Building24Regular className={styles.sectionIcon} />
              Kontaktdaten
            </h3>
            <p className={styles.sectionHint}>
              Bitte prüfen und gegebenenfalls korrigieren Sie die hinterlegten Stammdaten.
            </p>

            <QuestionBlock
              question="Sind die hinterlegten Stammdaten korrekt?"
              hint="Bitte prüfen Sie alle Felder und passen Sie diese bei Bedarf an."
              twoCol
            >
              <Field label="Name / Firma">
                <Input className={styles.fullWidth} value={name} onChange={(_, d) => setName(d.value)} />
              </Field>
              <Field label="Telefon">
                <FluentTelefonInput className={styles.fullWidth} value={telefon} onChange={setTelefon} geschaeftlich />
              </Field>
              <Field label="Straße & Hausnummer">
                <Input className={styles.fullWidth} value={strasse} onChange={(_, d) => setStrasse(d.value)} />
              </Field>
              <Field label="Fax">
                <FluentTelefonInput className={styles.fullWidth} value={fax} onChange={setFax} geschaeftlich zentrale={telefon} />
              </Field>
              <Field label="PLZ">
                <Input className={styles.fullWidth} value={plz} onChange={(_, d) => setPlz(d.value)} />
              </Field>
              <Field label="E-Mail">
                <Input type="email" className={styles.fullWidth} value={mail} onChange={(_, d) => setMail(d.value)} />
              </Field>
              <Field label="Ort">
                <Input className={styles.fullWidth} value={stadt} onChange={(_, d) => setStadt(d.value)} />
              </Field>
              <Field label="Webseite">
                <Input className={styles.fullWidth} value={webseite} onChange={(_, d) => setWebseite(d.value)} />
              </Field>
              <Field label="Land">
                <Input className={styles.fullWidth} value={land} onChange={(_, d) => setLand(d.value)} />
              </Field>
              <div />
              <div style={{ gridColumn: "1 / -1", marginTop: 8 }}>
                <Field label="Stammdaten korrekt?">
                  {renderRadioJaNein(kontaktdatenKorrekt, setKontaktdatenKorrekt)}
                </Field>
              </div>
            </QuestionBlock>
          </div>

          {/* ─────────── Section 2: Gründung ─────────── */}
          <div className={styles.sectionWrap}>
            <h3 className={styles.sectionHeader}>
              <DocumentRibbon24Regular className={styles.sectionIcon} />
              Informationen zum Unternehmen und zur Gründung
            </h3>

            <QuestionBlock question="Wann wurde Ihr Unternehmen gegründet?">
              <Field label="Gründungsjahr">
                <Input className={styles.fullWidth} value={gruendungsjahr} onChange={(_, d) => setGruendungsjahr(d.value)} />
              </Field>
            </QuestionBlock>

            <QuestionBlock question="Wer war der Gründer des Unternehmens?">
              <Field label="Name des Gründers">
                <Input className={styles.fullWidth} value={gruender} onChange={(_, d) => setGruender(d.value)} />
              </Field>
            </QuestionBlock>

            <QuestionBlock question="Welche Rechtsform hat Ihr Unternehmen?">
              <Field label="Rechtsform">
                <Input className={styles.fullWidth} value={rechtsform} onChange={(_, d) => setRechtsform(d.value)} />
              </Field>
            </QuestionBlock>

            <QuestionBlock question="Wie lautet die Handelsregisternummer und in welchem Register ist das Unternehmen eingetragen?">
              <Field label="Handelsregisternummer">
                <Input className={styles.fullWidth} value={handelsregisternummer} onChange={(_, d) => setHandelsregisternummer(d.value)} />
              </Field>
              <Field label="Register">
                <Select value={register} onChange={(e) => setRegister(e.target.value)}>
                  <option value="">— wählen —</option>
                  <option value="HRA">HRA – Handelsregister A</option>
                  <option value="HRB">HRB – Handelsregister B</option>
                  <option value="PR">PR – Partnerschaftsregister</option>
                  <option value="GnR">GnR – Genossenschaftsregister</option>
                  <option value="VR">VR – Vereinsregister</option>
                </Select>
              </Field>
            </QuestionBlock>

            <QuestionBlock question="Wie lautet Ihre Steuernummer, Ihre Umsatzsteuernummer und welches Finanzamt ist für Ihr Unternehmen zuständig?">
              <Field label="Steuernummer">
                <Input className={styles.fullWidth} value={steuernummer} onChange={(_, d) => setSteuernummer(d.value)} />
              </Field>
              <Field label="Umsatzsteuer-Identifikationsnummer">
                <Input className={styles.fullWidth} value={umsatzsteuernummer} onChange={(_, d) => setUmsatzsteuernummer(d.value)} />
              </Field>
              <Field label="Zuständiges Finanzamt">
                <Input className={styles.fullWidth} value={finanzamt} onChange={(_, d) => setFinanzamt(d.value)} />
              </Field>
            </QuestionBlock>

            <QuestionBlock question="Ist das Unternehmen vorsteuerabzugsberechtigt?">
              {renderRadioJaNein(vorsteuerabzug, setVorsteuerabzug)}
            </QuestionBlock>

            <QuestionBlock question="Ist das Geschäftsjahr gleich dem Kalenderjahr?">
              {renderRadioJaNein(geschaeftsjahrKalender, setGeschaeftsjahrKalender)}
              {ifNo(
                geschaeftsjahrKalender,
                <Field label="Abweichendes Geschäftsjahr (Monat / Tag, Details)">
                  <Input className={styles.fullWidth} value={geschaeftsjahrAbweichung} onChange={(_, d) => setGeschaeftsjahrAbweichung(d.value)} />
                </Field>
              )}
            </QuestionBlock>

            <QuestionBlock question="Ist das Unternehmen an weiteren Unternehmen beteiligt?">
              {renderRadioJaNein(beteiligungenJaNein, setBeteiligungenJaNein)}
              {ifYes(
                beteiligungenJaNein,
                <Field label="An welchen Unternehmen?">
                  <Textarea className={styles.fullWidth} value={beteiligungen} onChange={(_, d) => setBeteiligungen(d.value)} resize="vertical" />
                </Field>
              )}
            </QuestionBlock>

            <QuestionBlock question="Gibt es rechtlich selbständige oder unselbständige Niederlassungen?">
              {renderRadioJaNein(niederlassungenJaNein, setNiederlassungenJaNein)}
              {ifYes(
                niederlassungenJaNein,
                <Field label="Welche Niederlassungen?">
                  <Textarea className={styles.fullWidth} value={niederlassungen} onChange={(_, d) => setNiederlassungen(d.value)} resize="vertical" />
                </Field>
              )}
            </QuestionBlock>

            <QuestionBlock question="Gibt es einen Rechtsvorgänger des heutigen Unternehmens?">
              {renderRadioJaNein(rechtsvorgaengerJaNein, setRechtsvorgaengerJaNein)}
              {ifYes(
                rechtsvorgaengerJaNein,
                <Field label="Welcher Rechtsvorgänger?">
                  <Textarea className={styles.fullWidth} value={rechtsvorgaenger} onChange={(_, d) => setRechtsvorgaenger(d.value)} resize="vertical" />
                </Field>
              )}
            </QuestionBlock>
          </div>

          {/* ─────────── Section 3: Gesellschafter & Geschäftsführer ─────────── */}
          <div className={styles.sectionWrap}>
            <h3 className={styles.sectionHeader}>
              <PersonAvailable24Regular className={styles.sectionIcon} />
              Gesellschafter und Geschäftsführer
            </h3>
            <p className={styles.sectionHint}>
              Bitte Nachname, Vorname, Geburtsdatum und Kontaktdaten angeben. Bei Gesellschaftern
              zusätzlich die Anteile.
            </p>

            <div className={styles.subSection}>
              <h4 className={styles.subSectionTitle}>
                <PersonAvailable24Regular /> Geschäftsführer
              </h4>
              {geschaeftsfuehrer.map((p, idx) => (
                <div key={`gf-${idx}`} className={styles.fields} style={{ marginBottom: 8 }}>
                  <Field label={`#${idx + 1} Nachname`}>
                    <Input className={styles.fullWidth} value={p.nachname} onChange={(_, d) => updatePerson(geschaeftsfuehrer, setGeschaeftsfuehrer)(idx, "nachname", d.value)} />
                  </Field>
                  <Field label="Vorname">
                    <Input className={styles.fullWidth} value={p.vorname} onChange={(_, d) => updatePerson(geschaeftsfuehrer, setGeschaeftsfuehrer)(idx, "vorname", d.value)} />
                  </Field>
                  <Field label="Geburtsdatum">
                    <Input type="date" className={styles.fullWidth} value={p.geburtsdatum} onChange={(_, d) => updatePerson(geschaeftsfuehrer, setGeschaeftsfuehrer)(idx, "geburtsdatum", d.value)} />
                  </Field>
                  <Field label="Kontaktdaten">
                    <Input className={styles.fullWidth} value={p.kontaktdaten} onChange={(_, d) => updatePerson(geschaeftsfuehrer, setGeschaeftsfuehrer)(idx, "kontaktdaten", d.value)} />
                  </Field>
                </div>
              ))}
              <Button className={styles.rowAddButton} appearance="secondary" onClick={addPerson(geschaeftsfuehrer, setGeschaeftsfuehrer)}>
                + Geschäftsführer hinzufügen
              </Button>
            </div>

            <div className={styles.subSection}>
              <h4 className={styles.subSectionTitle}>
                <PersonAvailable24Regular /> Gesellschafter
              </h4>
              <p className={styles.sectionHint}>
                Alternativ kann ein Gesellschafter auch eine Firma sein. In dem Fall bitte Name der
                Gesellschaft, Sitz, sowie wer dort wiederum Gesellschafter und Geschäftsführer ist
                im Feld „Firma" und „Kontaktdaten" beschreiben.
              </p>
              {gesellschafter.map((p, idx) => (
                <div key={`gs-${idx}`} className={styles.fields} style={{ marginBottom: 8 }}>
                  <Field label={`#${idx + 1} Nachname (oder Firma)`}>
                    <Input className={styles.fullWidth} value={p.nachname} onChange={(_, d) => updatePerson(gesellschafter, setGesellschafter)(idx, "nachname", d.value)} />
                  </Field>
                  <Field label="Vorname">
                    <Input className={styles.fullWidth} value={p.vorname} onChange={(_, d) => updatePerson(gesellschafter, setGesellschafter)(idx, "vorname", d.value)} />
                  </Field>
                  <Field label="Geburtsdatum">
                    <Input type="date" className={styles.fullWidth} value={p.geburtsdatum} onChange={(_, d) => updatePerson(gesellschafter, setGesellschafter)(idx, "geburtsdatum", d.value)} />
                  </Field>
                  <Field label="Kontaktdaten / Firmensitz">
                    <Input className={styles.fullWidth} value={p.kontaktdaten} onChange={(_, d) => updatePerson(gesellschafter, setGesellschafter)(idx, "kontaktdaten", d.value)} />
                  </Field>
                  <Field label="Anteile">
                    <Input className={styles.fullWidth} value={p.anteile ?? ""} onChange={(_, d) => updatePerson(gesellschafter, setGesellschafter)(idx, "anteile", d.value)} />
                  </Field>
                  <Field label="Firma (falls Gesellschafter eine Firma ist)">
                    <Input className={styles.fullWidth} value={p.firma ?? ""} onChange={(_, d) => updatePerson(gesellschafter, setGesellschafter)(idx, "firma", d.value)} />
                  </Field>
                </div>
              ))}
              <Button className={styles.rowAddButton} appearance="secondary" onClick={addPerson(gesellschafter, setGesellschafter)}>
                + Gesellschafter hinzufügen
              </Button>
            </div>

            <QuestionBlock question="Haben die Gesellschafter weitere Unternehmensbeteiligungen?">
              {renderRadioJaNein(weitereBeteiligungenJaNein, setWeitereBeteiligungenJaNein)}
              {ifYes(
                weitereBeteiligungenJaNein,
                <Field label="Welche Beteiligungen?">
                  <Textarea className={styles.fullWidth} value={weitereBeteiligungenGesellschafter} onChange={(_, d) => setWeitereBeteiligungenGesellschafter(d.value)} resize="vertical" />
                </Field>
              )}
            </QuestionBlock>

            <QuestionBlock question="Gibt es spezielle Versorgungen oder Absicherungen für die Geschäftsführer und/oder Gesellschafter?">
              {renderRadioJaNein(spezielleVersorgungenJaNein, setSpezielleVersorgungenJaNein)}
              {ifYes(
                spezielleVersorgungenJaNein,
                <Field label="Was, wo, in € (bitte die Neuwerte)">
                  <Textarea className={styles.fullWidth} value={spezielleVersorgungen} onChange={(_, d) => setSpezielleVersorgungen(d.value)} resize="vertical" />
                </Field>
              )}
            </QuestionBlock>

            <QuestionBlock question="Sind Veränderungen zum Unternehmen, zu den Gesellschafterstrukturen oder Beteiligungen angedacht?">
              {renderRadioJaNein(veraenderungenGesellschaftJaNein, setVeraenderungenGesellschaftJaNein)}
              {ifYes(
                veraenderungenGesellschaftJaNein,
                <Field label="Welche Veränderungen?">
                  <Textarea className={styles.fullWidth} value={veraenderungenGesellschaft} onChange={(_, d) => setVeraenderungenGesellschaft(d.value)} resize="vertical" />
                </Field>
              )}
            </QuestionBlock>

            <div className={styles.subSection}>
              <h4 className={styles.subSectionTitle}>
                <PersonAvailable24Regular /> Zuständigkeiten
              </h4>
              <p className={styles.sectionHint}>
                Wer ist intern oder extern für was zuständig (Steuerberater, Rechtsanwalt, Bank,
                Sicherheits-/Brandschutz-/Compliance-/Datenschutz-/IT-Beauftragter, Arbeitsschutz, …)?
              </p>
              {zustaendigkeiten.map((p, idx) => (
                <div key={`zu-${idx}`} className={styles.fields} style={{ marginBottom: 8 }}>
                  <Field label={`#${idx + 1} Rolle / Bereich`}>
                    <Input className={styles.fullWidth} value={p.rolle ?? ""} onChange={(_, d) => updatePerson(zustaendigkeiten, setZustaendigkeiten)(idx, "rolle", d.value)} placeholder="z. B. Steuerberater" />
                  </Field>
                  <Field label="Nachname / Vorname">
                    <Input className={styles.fullWidth} value={p.nachname} onChange={(_, d) => updatePerson(zustaendigkeiten, setZustaendigkeiten)(idx, "nachname", d.value)} />
                  </Field>
                  <Field label="Geburtsdatum">
                    <Input type="date" className={styles.fullWidth} value={p.geburtsdatum} onChange={(_, d) => updatePerson(zustaendigkeiten, setZustaendigkeiten)(idx, "geburtsdatum", d.value)} />
                  </Field>
                  <Field label="Kontaktdaten">
                    <Input className={styles.fullWidth} value={p.kontaktdaten} onChange={(_, d) => updatePerson(zustaendigkeiten, setZustaendigkeiten)(idx, "kontaktdaten", d.value)} />
                  </Field>
                  <Field label="Firma">
                    <Input className={styles.fullWidth} value={p.firma ?? ""} onChange={(_, d) => updatePerson(zustaendigkeiten, setZustaendigkeiten)(idx, "firma", d.value)} />
                  </Field>
                </div>
              ))}
              <Button className={styles.rowAddButton} appearance="secondary" onClick={addPerson(zustaendigkeiten, setZustaendigkeiten)}>
                + Zuständigkeit hinzufügen
              </Button>
            </div>

            <QuestionBlock question="Gibt es in Ihrem Unternehmen ein Qualitätsmanagement (z. B. ISO 9001)?">
              {renderRadioJaNein(qualitaetsmanagementJaNein, setQualitaetsmanagementJaNein)}
              {ifYes(
                qualitaetsmanagementJaNein,
                <>
                  <Field label="Welches Qualitätsmanagement?">
                    <Input className={styles.fullWidth} value={qualitaetsmanagement} onChange={(_, d) => setQualitaetsmanagement(d.value)} />
                  </Field>
                  <Field label="Verantwortlich (Name, Firma, Kontakt)">
                    <Input className={styles.fullWidth} value={qmVerantwortlich} onChange={(_, d) => setQmVerantwortlich(d.value)} />
                  </Field>
                </>
              )}
            </QuestionBlock>
          </div>

          {/* ─────────── Section 4: Tätigkeit / Kunden / Lieferanten ─────────── */}
          <div className={styles.sectionWrap}>
            <h3 className={styles.sectionHeader}>
              <Briefcase24Regular className={styles.sectionIcon} />
              Tätigkeit, Kunden und Lieferanten
            </h3>

            <QuestionBlock
              question="Was macht Ihr Unternehmen genau? (Tätigkeit / Betriebscharakter)"
              hint="Bitte in eigenen Worten formulieren."
            >
              <Field label="Beschreibung">
                <Textarea className={styles.fullWidth} value={taetigkeit} onChange={(_, d) => setTaetigkeit(d.value)} resize="vertical" />
              </Field>
            </QuestionBlock>

            <QuestionBlock question="Gibt es darüber hinaus noch etwas?">
              <Field label="Ergänzungen">
                <Textarea className={styles.fullWidth} value={taetigkeitWeiteres} onChange={(_, d) => setTaetigkeitWeiteres(d.value)} resize="vertical" />
              </Field>
            </QuestionBlock>

            <QuestionBlock question="Aus welchem Bereich kommen Ihre Kunden — haben Sie B2B-Kunden?">
              {renderRadioJaNein(b2b, setB2b)}
              {ifYes(
                b2b,
                <Field label="Details (z. B. Kommunal, Anteil)">
                  <Input className={styles.fullWidth} value={b2bDetails} onChange={(_, d) => setB2bDetails(d.value)} />
                </Field>
              )}
            </QuestionBlock>

            <QuestionBlock question="Haben Sie B2C-Kunden?">
              {renderRadioJaNein(b2c, setB2c)}
              {ifYes(
                b2c,
                <Field label="Details (Anteil)">
                  <Input className={styles.fullWidth} value={b2cDetails} onChange={(_, d) => setB2cDetails(d.value)} />
                </Field>
              )}
            </QuestionBlock>

            <QuestionBlock question="Haben Sie sonstige Kundengruppen?">
              {renderRadioJaNein(sonstigeKunden, setSonstigeKunden)}
              {ifYes(
                sonstigeKunden,
                <Field label="Welche Kundengruppen?">
                  <Input className={styles.fullWidth} value={sonstigeKundenDetails} onChange={(_, d) => setSonstigeKundenDetails(d.value)} />
                </Field>
              )}
            </QuestionBlock>

            <QuestionBlock question="Gibt es Abhängigkeiten zu Kunden?">
              {renderRadioJaNein(abhaengigkeitenKunden, setAbhaengigkeitenKunden)}
            </QuestionBlock>

            <QuestionBlock question="Wo ist der Vertriebs- und Kunden-Schwerpunkt?">
              <Field label="Schwerpunkt">
                <Textarea className={styles.fullWidth} value={vertriebsschwerpunkt} onChange={(_, d) => setVertriebsschwerpunkt(d.value)} resize="vertical" />
              </Field>
            </QuestionBlock>

            <QuestionBlock question="Wie informieren Sie Kunden über Neuerungen oder sonstiges (z. B. Newsletter)?">
              <Field label="Kundeninformation">
                <Textarea className={styles.fullWidth} value={kundeninformation} onChange={(_, d) => setKundeninformation(d.value)} resize="vertical" />
              </Field>
            </QuestionBlock>

            <QuestionBlock
              question="Wer sind Ihre Hauptlieferanten und wie verteilen sich die Umsätze auf diese?"
            >
              <Field label="Lieferanten und Verteilung">
                <Textarea className={styles.fullWidth} value={hauptlieferanten} onChange={(_, d) => setHauptlieferanten(d.value)} resize="vertical" />
              </Field>
            </QuestionBlock>

            <QuestionBlock question="Gibt es Abhängigkeiten zu Lieferanten?">
              {renderRadioJaNein(abhaengigkeitenLieferanten, setAbhaengigkeitenLieferanten)}
            </QuestionBlock>
          </div>

          {/* ─────────── Section 5: Risks ─────────── */}
          <div className={styles.sectionWrap}>
            <h3 className={styles.sectionHeader}>
              <ShieldTask24Regular className={styles.sectionIcon} />
              Haftungs-, Finanz- und Rechtsstreitigkeitsrisiken
            </h3>
            <p className={styles.sectionHint}>
              Sehen Sie aus dem, was Sie tun, die folgenden Risiken? Bei „Ja" erscheint ein
              optionales Detailfeld, in dem Sie das Risiko näher beschreiben können.
            </p>
            <QuestionBlock question="Allgemeine Anmerkungen zu Ihren Risiken">
              <Field label="Anmerkungen">
                <Textarea className={styles.fullWidth} value={risikenAllgemein} onChange={(_, d) => setRisikenAllgemein(d.value)} resize="vertical" />
              </Field>
            </QuestionBlock>
            {RISK_QUESTIONS.map((q) => {
              const answer = riskAnswers[q.key] ?? "";
              return (
                <QuestionBlock key={q.key} question={q.label}>
                  <RadioGroup
                    value={answer}
                    onChange={(_, data) => setRisk(q.key, data.value)}
                    layout="horizontal"
                  >
                    <Radio value="ja" label="Ja" />
                    <Radio value="nein" label="Nein" />
                  </RadioGroup>
                  {ifYes(
                    answer,
                    <Field label={q.detailsLabel ?? "Bitte näher beschreiben"}>
                      <Textarea
                        className={styles.fullWidth}
                        value={riskNotes[q.key] ?? ""}
                        onChange={(_, d) => setRiskNote(q.key, d.value)}
                        resize="vertical"
                      />
                    </Field>
                  )}
                </QuestionBlock>
              );
            })}
          </div>

          {/* ─────────── Section 6: Locations ─────────── */}
          <div className={styles.sectionWrap}>
            <h3 className={styles.sectionHeader}>
              <Home24Regular className={styles.sectionIcon} />
              Betriebsstätten und Lager
            </h3>
            <p className={styles.sectionHint}>
              Bitte erfassen Sie alle Standorte (Betriebsstätten, Lager, Sonstiges) Ihres Unternehmens.
            </p>
            {standorte.map((s, idx) => {
              const sicherungen: Array<{
                key: string;
                label: string;
                jaField: keyof Standort;
                wasField: keyof Standort;
                woField?: keyof Standort;
              }> = [
                { key: "einfriedung", label: "Gibt es eine Einfriedung?", jaField: "einfriedungJa", wasField: "einfriedung", woField: "einfriedungWo" },
                { key: "einbruchmeldeanlage", label: "Gibt es eine Einbruchmeldeanlage / Echtzeitüberwachung?", jaField: "einbruchmeldeanlageJa", wasField: "einbruchmeldeanlage", woField: "einbruchmeldeanlageWo" },
                { key: "brandmeldeanlage", label: "Gibt es eine Brandmeldeanlage?", jaField: "brandmeldeanlageJa", wasField: "brandmeldeanlage", woField: "brandmeldeanlageWo" },
                { key: "rwa", label: "Gibt es eine RWA (Rauch- und Wärmeabzugsanlage)?", jaField: "rwaJa", wasField: "rwa", woField: "rwaWo" },
                { key: "sprinkler", label: "Gibt es eine Sprinkleranlage?", jaField: "sprinklerJa", wasField: "sprinkler", woField: "sprinklerWo" },
                { key: "stromabschaltung", label: "Gibt es eine Stromabschaltung?", jaField: "stromabschaltungJa", wasField: "stromabschaltung", woField: "stromabschaltungWo" },
                { key: "videoueberwachung", label: "Gibt es eine Videoüberwachung?", jaField: "videoueberwachungJa", wasField: "videoueberwachung", woField: "videoueberwachungWo" },
                { key: "notstrom", label: "Gibt es eine Notstromversorgung?", jaField: "notstromJa", wasField: "notstrom", woField: "notstromWo" },
                { key: "sonstigeSicherung", label: "Gibt es sonstige Sicherungen?", jaField: "sonstigeSicherungJa", wasField: "sonstigeSicherung" },
              ];
              return (
                <div key={`standort-${idx}`} className={styles.subSection}>
                  <h4 className={styles.subSectionTitle}>
                    <Home24Regular /> Standort {idx + 1}
                  </h4>

                  <QuestionBlock question="Wo befindet sich der Standort?">
                    <Field label="Ort">
                      <Input className={styles.fullWidth} value={s.ort} onChange={(_, d) => updateStandort(idx, "ort", d.value)} />
                    </Field>
                    <Field label="PLZ">
                      <Input className={styles.fullWidth} value={s.plz} onChange={(_, d) => updateStandort(idx, "plz", d.value)} />
                    </Field>
                    <Field label="Straße">
                      <Input className={styles.fullWidth} value={s.strasse} onChange={(_, d) => updateStandort(idx, "strasse", d.value)} />
                    </Field>
                    <Field label="Hausnummer">
                      <Input className={styles.fullWidth} value={s.hausnummer} onChange={(_, d) => updateStandort(idx, "hausnummer", d.value)} />
                    </Field>
                  </QuestionBlock>

                  <QuestionBlock question="Handelt es sich um Eigentum oder ist die Immobilie angemietet, gepachtet oder geleast?">
                    <Field label="Nutzungsart">
                      <Select value={s.eigentum} onChange={(e) => updateStandort(idx, "eigentum", e.target.value)}>
                        <option value="">— wählen —</option>
                        <option value="eigentum">Eigentum</option>
                        <option value="miete">Miete</option>
                        <option value="pacht">Pacht</option>
                        <option value="leasing">Leasing</option>
                      </Select>
                    </Field>
                    {s.eigentum && s.eigentum !== "eigentum" && (
                      <div className={styles.reveal}>
                        <Field label="Wer ist Eigentümer? Wer trägt das Risiko an den betrieblich genutzten Räumen?">
                          <Input className={styles.fullWidth} value={s.eigentuemer} onChange={(_, d) => updateStandort(idx, "eigentuemer", d.value)} />
                        </Field>
                      </div>
                    )}
                  </QuestionBlock>

                  <QuestionBlock question="Wie groß sind die Flächen?">
                    <Field label="Fläche insgesamt (m²)">
                      <Input className={styles.fullWidth} value={s.flaecheGesamt} onChange={(_, d) => updateStandort(idx, "flaecheGesamt", d.value)} />
                    </Field>
                    <Field label="Davon selbst genutzt (m²)">
                      <Input className={styles.fullWidth} value={s.flaecheSelbst} onChange={(_, d) => updateStandort(idx, "flaecheSelbst", d.value)} />
                    </Field>
                  </QuestionBlock>

                  <QuestionBlock question="Erfolgt eine Untervermietung?">
                    {renderRadioJaNein(s.untervermietung, (v) => updateStandort(idx, "untervermietung", v))}
                  </QuestionBlock>

                  <QuestionBlock
                    question="Wurden Grundstücksbestandteile von Ihnen eingebracht?"
                    hint="Ist fremdes Eigentum mitzuversichern?"
                  >
                    {renderRadioJaNein(s.grundstuecksbestandteile, (v) => updateStandort(idx, "grundstuecksbestandteile", v))}
                  </QuestionBlock>

                  <QuestionBlock question="Welche Heizung haben Sie am Standort?">
                    <Field label="Heizung">
                      <Input className={styles.fullWidth} value={s.heizung} onChange={(_, d) => updateStandort(idx, "heizung", d.value)} />
                    </Field>
                  </QuestionBlock>

                  <QuestionBlock question="Ist ein Trafo vorhanden?">
                    {renderRadioJaNein(s.trafo, (v) => updateStandort(idx, "trafo", v))}
                  </QuestionBlock>

                  <QuestionBlock question="Handelt es sich auch um den Firmensitz?">
                    {renderRadioJaNein(s.istFirmensitz, (v) => updateStandort(idx, "istFirmensitz", v))}
                  </QuestionBlock>

                  <QuestionBlock question="Handelt es sich auch um die Postanschrift?">
                    {renderRadioJaNein(s.istPostanschrift, (v) => updateStandort(idx, "istPostanschrift", v))}
                  </QuestionBlock>

                  <h4 className={styles.subSectionTitle} style={{ marginTop: 14 }}>
                    <ShieldTask24Regular /> Sicherungen und Präventionen
                  </h4>

                  {sicherungen.map((sicherung) => {
                    const ja = s[sicherung.jaField] as YesNo;
                    return (
                      <QuestionBlock key={sicherung.key} question={sicherung.label}>
                        {renderRadioJaNein(ja, (v) => updateStandort(idx, sicherung.jaField, v))}
                        {ifYes(
                          ja,
                          <>
                            <Field label="Was?">
                              <Input
                                className={styles.fullWidth}
                                value={s[sicherung.wasField] as string}
                                onChange={(_, d) => updateStandort(idx, sicherung.wasField, d.value)}
                              />
                            </Field>
                            {sicherung.woField && (
                              <Field label="Wo?">
                                <Input
                                  className={styles.fullWidth}
                                  value={s[sicherung.woField] as string}
                                  onChange={(_, d) => updateStandort(idx, sicherung.woField!, d.value)}
                                />
                              </Field>
                            )}
                          </>
                        )}
                      </QuestionBlock>
                    );
                  })}

                  <QuestionBlock question="Gibt es einen Notfallplan im Schadenfall?">
                    {renderRadioJaNein(s.notfallplan, (v) => updateStandort(idx, "notfallplan", v))}
                  </QuestionBlock>

                  <QuestionBlock question="Wie lange benötigen Sie, um Ihren Betrieb nach einem Schadenfall wieder fortsetzen zu können?">
                    <Field label="Wiederaufnahmedauer">
                      <Input className={styles.fullWidth} value={s.wiederaufnahmedauer} onChange={(_, d) => updateStandort(idx, "wiederaufnahmedauer", d.value)} placeholder="z. B. 2 Wochen" />
                    </Field>
                  </QuestionBlock>

                  <QuestionBlock question="Gibt es weitere Präventions- oder Schadenabwendungsmaßnahmen?">
                    {renderRadioJaNein(s.weiterePraeventionJa, (v) => updateStandort(idx, "weiterePraeventionJa", v))}
                    {ifYes(
                      s.weiterePraeventionJa,
                      <Field label="Welche Maßnahmen?">
                        <Textarea className={styles.fullWidth} value={s.weiterePraevention} onChange={(_, d) => updateStandort(idx, "weiterePraevention", d.value)} resize="vertical" />
                      </Field>
                    )}
                  </QuestionBlock>
                </div>
              );
            })}
            <Button className={styles.rowAddButton} appearance="secondary" onClick={() => setStandorte([...standorte, emptyStandort()])}>
              + Weiteren Standort hinzufügen
            </Button>

            <QuestionBlock question="Sind Veränderungen, Erweiterungen oder Investitionen an den Standorten geplant?">
              {renderRadioJaNein(investitionenStandorteGeplant, setInvestitionenStandorteGeplant)}
              {ifYes(
                investitionenStandorteGeplant,
                <Field label="Welche?">
                  <Input className={styles.fullWidth} value={investitionenStandorteDetails} onChange={(_, d) => setInvestitionenStandorteDetails(d.value)} />
                </Field>
              )}
            </QuestionBlock>
          </div>

          {/* ─────────── Section 7: Assets ─────────── */}
          <div className={styles.sectionWrap}>
            <h3 className={styles.sectionHeader}>
              <Briefcase24Regular className={styles.sectionIcon} />
              Vermögenswerte
            </h3>
            <p className={styles.sectionHint}>
              Bitte geben Sie pro Kategorie an, ob vorhanden, was, wo und den Neuwert in €.
            </p>
            {ASSET_CATEGORIES.map((c) => {
              const a = assets[c.key] ?? { vorhanden: "" as YesNo, was: "", wo: "", wert: "" };
              return (
                <QuestionBlock key={c.key} question={`Hat Ihr Unternehmen ${c.label}?`}>
                  <RadioGroup
                    value={a.vorhanden}
                    onChange={(_, data) => setAssetField(c.key, "vorhanden", data.value)}
                    layout="horizontal"
                  >
                    <Radio value="ja" label="Ja" />
                    <Radio value="nein" label="Nein" />
                  </RadioGroup>
                  {ifYes(
                    a.vorhanden,
                    <>
                      <Field label="Was?">
                        <Input className={styles.fullWidth} value={a.was} onChange={(_, d) => setAssetField(c.key, "was", d.value)} />
                      </Field>
                      <Field label="Wo?">
                        <Input className={styles.fullWidth} value={a.wo} onChange={(_, d) => setAssetField(c.key, "wo", d.value)} />
                      </Field>
                      <Field label="Neuwert in €">
                        <Input className={styles.fullWidth} value={a.wert} onChange={(_, d) => setAssetField(c.key, "wert", d.value)} />
                      </Field>
                    </>
                  )}
                </QuestionBlock>
              );
            })}

            <QuestionBlock question="Haben Sie eigene Kfz?">
              {renderRadioJaNein(fahrzeugeEigeneJa, setFahrzeugeEigeneJa)}
              {ifYes(
                fahrzeugeEigeneJa,
                <>
                  <Field label="Was für Fahrzeuge?">
                    <Input className={styles.fullWidth} value={fahrzeugeEigene.was} onChange={(_, d) => setFahrzeugeEigene((p) => ({ ...p, was: d.value }))} />
                  </Field>
                  <Field label="Wie viele?">
                    <Input className={styles.fullWidth} value={fahrzeugeEigene.anzahl} onChange={(_, d) => setFahrzeugeEigene((p) => ({ ...p, anzahl: d.value }))} />
                  </Field>
                </>
              )}
            </QuestionBlock>

            <QuestionBlock question="Sind fremde Kfz vorhanden?">
              {renderRadioJaNein(fahrzeugeFremde, setFahrzeugeFremde)}
              {ifYes(
                fahrzeugeFremde,
                <>
                  <Field label="Was für Fahrzeuge?">
                    <Input className={styles.fullWidth} value={fahrzeugeFremdeDetails.was} onChange={(_, d) => setFahrzeugeFremdeDetails((p) => ({ ...p, was: d.value }))} />
                  </Field>
                  <Field label="Wie viele?">
                    <Input className={styles.fullWidth} value={fahrzeugeFremdeDetails.anzahl} onChange={(_, d) => setFahrzeugeFremdeDetails((p) => ({ ...p, anzahl: d.value }))} />
                  </Field>
                </>
              )}
            </QuestionBlock>

            <QuestionBlock question="Haben Sie sonstige Fahrzeuge?">
              {renderRadioJaNein(fahrzeugeSonstigeJa, setFahrzeugeSonstigeJa)}
              {ifYes(
                fahrzeugeSonstigeJa,
                <>
                  <Field label="Was für Fahrzeuge?">
                    <Input className={styles.fullWidth} value={fahrzeugeSonstige.was} onChange={(_, d) => setFahrzeugeSonstige((p) => ({ ...p, was: d.value }))} />
                  </Field>
                  <Field label="Wie viele?">
                    <Input className={styles.fullWidth} value={fahrzeugeSonstige.anzahl} onChange={(_, d) => setFahrzeugeSonstige((p) => ({ ...p, anzahl: d.value }))} />
                  </Field>
                </>
              )}
            </QuestionBlock>

            <QuestionBlock question="Sind vermögensseitige Veränderungen / Erweiterungen / Investitionen geplant?">
              {renderRadioJaNein(vermoegenInvestitionenGeplant, setVermoegenInvestitionenGeplant)}
              {ifYes(
                vermoegenInvestitionenGeplant,
                <Field label="Welche Investitionen?">
                  <Input className={styles.fullWidth} value={vermoegenInvestitionenDetails} onChange={(_, d) => setVermoegenInvestitionenDetails(d.value)} />
                </Field>
              )}
            </QuestionBlock>
          </div>

          {/* ─────────── Section 8: Transport & KPIs ─────────── */}
          <div className={styles.sectionWrap}>
            <h3 className={styles.sectionHeader}>
              <VehicleCar24Regular className={styles.sectionIcon} />
              Versand und Bezug
            </h3>

            <QuestionBlock question="Versenden Sie Waren?">
              {renderRadioJaNein(versendenWaren, setVersendenWaren)}
              {ifYes(
                versendenWaren,
                <>
                  <Field label="Wie kommen die Waren zum Empfänger?">
                    <Textarea className={styles.fullWidth} value={versendenWie} onChange={(_, d) => setVersendenWie(d.value)} resize="vertical" />
                  </Field>
                  <Field label="Mit welchen Fahrzeugen / Mitteln?" hint="PKW, LKW, Lieferwagen, Anhänger, Bahn, Spedition, Schiff, Flugzeug, Paketservice …">
                    <Input className={styles.fullWidth} value={versendenFahrzeuge} onChange={(_, d) => setVersendenFahrzeuge(d.value)} />
                  </Field>
                  <Field label="Wer trägt die Gefahr?">
                    <Input className={styles.fullWidth} value={versendenGefahr} onChange={(_, d) => setVersendenGefahr(d.value)} />
                  </Field>
                  <Field label="Welchen Wert haben die Versendungen pro Transportmittel (€)?">
                    <Input className={styles.fullWidth} value={versendenWert} onChange={(_, d) => setVersendenWert(d.value)} />
                  </Field>
                </>
              )}
            </QuestionBlock>

            <QuestionBlock question="Wie erhalten Sie nötige Betriebsmittel? (Bezüge)">
              <Field label="Beschreibung">
                <Textarea className={styles.fullWidth} value={erhaltenBetriebsmittel} onChange={(_, d) => setErhaltenBetriebsmittel(d.value)} resize="vertical" />
              </Field>
              <Field label="Mit welchen Mitteln kommen diese zu Ihnen?">
                <Input className={styles.fullWidth} value={erhaltenWie} onChange={(_, d) => setErhaltenWie(d.value)} />
              </Field>
              <Field label="Wer trägt die Gefahr?">
                <Input className={styles.fullWidth} value={erhaltenGefahr} onChange={(_, d) => setErhaltenGefahr(d.value)} />
              </Field>
              <Field label="Welchen Wert haben die Bezüge pro Transportmittel (€)?">
                <Input className={styles.fullWidth} value={erhaltenWert} onChange={(_, d) => setErhaltenWert(d.value)} />
              </Field>
            </QuestionBlock>

            <QuestionBlock question="Gibt es geplante Zwischenlagerungen?">
              <Field label="Beschreibung">
                <Input className={styles.fullWidth} value={zwischenlagerungen} onChange={(_, d) => setZwischenlagerungen(d.value)} />
              </Field>
            </QuestionBlock>

            <QuestionBlock question="Transportieren Sie auch für Dritte?">
              {renderRadioJaNein(transportFuerDritte, setTransportFuerDritte)}
            </QuestionBlock>

            <QuestionBlock question="Bieten Sie fremden Dritten Lager von Ihnen zur Nutzung an? (Sind Sie Lagerhalter?)">
              {renderRadioJaNein(lagerhalter, setLagerhalter)}
            </QuestionBlock>

            <QuestionBlock question="Nehmen Sie an Messen teil?">
              {renderRadioJaNein(messen, setMessen)}
            </QuestionBlock>
          </div>

          <div className={styles.sectionWrap}>
            <h3 className={styles.sectionHeader}>
              <Money24Regular className={styles.sectionIcon} />
              Kennzahlen (letzte zwei Jahre)
            </h3>

            <QuestionBlock question="Wie hoch waren Umsatz, Wareneinsatz und Rohertrag im letzten abgelaufenen Geschäftsjahr?">
              <Field label="Umsatz (€)"><Input className={styles.fullWidth} value={umsatz} onChange={(_, d) => setUmsatz(d.value)} /></Field>
              <Field label="Wareneinsatz (€)"><Input className={styles.fullWidth} value={wareneinsatz} onChange={(_, d) => setWareneinsatz(d.value)} /></Field>
              <Field label="Rohertrag (€)"><Input className={styles.fullWidth} value={rohertrag} onChange={(_, d) => setRohertrag(d.value)} /></Field>
            </QuestionBlock>

            <QuestionBlock question="Gibt es starke Schwankungen beim Umsatz?">
              {renderRadioJaNein(umsatzschwankungen, setUmsatzschwankungen)}
              {ifYes(
                umsatzschwankungen,
                <Field label="Warum?"><Input className={styles.fullWidth} value={umsatzschwankungenWarum} onChange={(_, d) => setUmsatzschwankungenWarum(d.value)} /></Field>
              )}
            </QuestionBlock>

            <QuestionBlock question="Gibt es Sondereffekte (z. B. Sondergeschäfte) beim Umsatz?">
              {renderRadioJaNein(sondereffekte, setSondereffekte)}
              {ifYes(
                sondereffekte,
                <Field label="Warum?"><Input className={styles.fullWidth} value={sondereffekteWarum} onChange={(_, d) => setSondereffekteWarum(d.value)} /></Field>
              )}
            </QuestionBlock>

            <QuestionBlock question="Gibt es Innenumsätze?">
              {renderRadioJaNein(innenumsaetze, setInnenumsaetze)}
              {ifYes(
                innenumsaetze,
                <Field label="Mit wem und wie hoch?"><Input className={styles.fullWidth} value={innenumsaetzeDetails} onChange={(_, d) => setInnenumsaetzeDetails(d.value)} /></Field>
              )}
            </QuestionBlock>

            <QuestionBlock question="Wie verteilt sich der Umsatz?">
              <Field label="Deutschland"><Input className={styles.fullWidth} value={umsatzDeutschland} onChange={(_, d) => setUmsatzDeutschland(d.value)} /></Field>
              <Field label="Europa"><Input className={styles.fullWidth} value={umsatzEuropa} onChange={(_, d) => setUmsatzEuropa(d.value)} /></Field>
              <Field label="Weltweit"><Input className={styles.fullWidth} value={umsatzWeltweit} onChange={(_, d) => setUmsatzWeltweit(d.value)} /></Field>
              <Field label="Davon USA / Kanada"><Input className={styles.fullWidth} value={umsatzUsaKanada} onChange={(_, d) => setUmsatzUsaKanada(d.value)} /></Field>
            </QuestionBlock>

            {[
              { label: "Automotive", value: brancheAutomotive, setter: setBrancheAutomotive },
              { label: "Schifffahrt", value: brancheSchifffahrt, setter: setBrancheSchifffahrt },
              { label: "Luftfahrt", value: brancheLuftfahrt, setter: setBrancheLuftfahrt },
              { label: "Schienenverkehr", value: brancheSchienenverkehr, setter: setBrancheSchienenverkehr },
            ].map((b) => (
              <QuestionBlock key={b.label} question={`Generieren Sie Umsatz mit ${b.label}?`}>
                <RadioGroup value={b.value.jaNein} onChange={(_, data) => b.setter({ ...b.value, jaNein: data.value as YesNo })} layout="horizontal">
                  <Radio value="ja" label="Ja" />
                  <Radio value="nein" label="Nein" />
                </RadioGroup>
                {ifYes(
                  b.value.jaNein,
                  <>
                    <Field label="Wieviel?"><Input className={styles.fullWidth} value={b.value.wieviel} onChange={(_, d) => b.setter({ ...b.value, wieviel: d.value })} /></Field>
                    <Field label="Warum?"><Input className={styles.fullWidth} value={b.value.warum} onChange={(_, d) => b.setter({ ...b.value, warum: d.value })} /></Field>
                  </>
                )}
              </QuestionBlock>
            ))}

            <QuestionBlock question="Gibt es bei Rohertrag und Wareneinsatz starke Schwankungen?">
              <RadioGroup value={rohertragSchwankungen.jaNein} onChange={(_, data) => setRohertragSchwankungen({ ...rohertragSchwankungen, jaNein: data.value as YesNo })} layout="horizontal">
                <Radio value="ja" label="Ja" />
                <Radio value="nein" label="Nein" />
              </RadioGroup>
              {ifYes(
                rohertragSchwankungen.jaNein,
                <>
                  <Field label="Wieviel?"><Input className={styles.fullWidth} value={rohertragSchwankungen.wieviel} onChange={(_, d) => setRohertragSchwankungen({ ...rohertragSchwankungen, wieviel: d.value })} /></Field>
                  <Field label="Warum?"><Input className={styles.fullWidth} value={rohertragSchwankungen.warum} onChange={(_, d) => setRohertragSchwankungen({ ...rohertragSchwankungen, warum: d.value })} /></Field>
                </>
              )}
            </QuestionBlock>

            <QuestionBlock question="Wie hoch ist die Wareneinsatzquote (in %)?">
              <Field label="Wareneinsatzquote"><Input className={styles.fullWidth} value={wareneinsatzquote} onChange={(_, d) => setWareneinsatzquote(d.value)} /></Field>
            </QuestionBlock>

            <QuestionBlock question="Wie hoch ist Ihr Eigenkapital?">
              <Field label="Eigenkapital (€)"><Input className={styles.fullWidth} value={eigenkapital} onChange={(_, d) => setEigenkapital(d.value)} /></Field>
            </QuestionBlock>

            <QuestionBlock question="Wie hoch ist Ihr Kredit- und Kontokorrentrahmen?">
              <Field label="Rahmen (€)"><Input className={styles.fullWidth} value={kreditrahmen} onChange={(_, d) => setKreditrahmen(d.value)} /></Field>
            </QuestionBlock>

            <QuestionBlock question="Welche Darlehen, Verbindlichkeiten oder sonstigen Verpflichtungen hat Ihr Unternehmen?">
              <Field label="Beschreibung"><Textarea className={styles.fullWidth} value={darlehnVerbindlichkeiten} onChange={(_, d) => setDarlehnVerbindlichkeiten(d.value)} resize="vertical" /></Field>
            </QuestionBlock>

            <QuestionBlock question="Geben Sie Bürgschaften? Wenn ja, wie hoch insgesamt und im Schnitt?">
              <Field label="Bürgschaften"><Input className={styles.fullWidth} value={buergschaften} onChange={(_, d) => setBuergschaften(d.value)} /></Field>
            </QuestionBlock>

            <QuestionBlock question="Wie hoch ist Ihre verfügbare Liquidität?">
              <Field label="Kurzfristig"><Input className={styles.fullWidth} value={liquiditaetKurz} onChange={(_, d) => setLiquiditaetKurz(d.value)} /></Field>
              <Field label="Mittelfristig"><Input className={styles.fullWidth} value={liquiditaetMittel} onChange={(_, d) => setLiquiditaetMittel(d.value)} /></Field>
              <Field label="Langfristig"><Input className={styles.fullWidth} value={liquiditaetLang} onChange={(_, d) => setLiquiditaetLang(d.value)} /></Field>
            </QuestionBlock>
          </div>

          {/* ─────────── Section 9: People ─────────── */}
          <div className={styles.sectionWrap}>
            <h3 className={styles.sectionHeader}>
              <People24Regular className={styles.sectionIcon} />
              Menschen in Ihrem Unternehmen
            </h3>

            <QuestionBlock question="Wie viele Menschen arbeiten insgesamt für Ihr Unternehmen?">
              <Field label="Insgesamt"><Input className={styles.fullWidth} value={mitarbeiterInsgesamt} onChange={(_, d) => setMitarbeiterInsgesamt(d.value)} /></Field>
              <Field label="Vollzeitkräfte"><Input className={styles.fullWidth} value={vollzeitkraefte} onChange={(_, d) => setVollzeitkraefte(d.value)} /></Field>
              <Field label="Monatsstunden Vollzeit"><Input className={styles.fullWidth} value={monatsstunden} onChange={(_, d) => setMonatsstunden(d.value)} /></Field>
              <Field label="Teilzeitkräfte"><Input className={styles.fullWidth} value={teilzeitkraefte} onChange={(_, d) => setTeilzeitkraefte(d.value)} /></Field>
              <Field label="Davon Minijobber"><Input className={styles.fullWidth} value={minijobber} onChange={(_, d) => setMinijobber(d.value)} /></Field>
              <Field label="Davon Midijobber"><Input className={styles.fullWidth} value={midijobber} onChange={(_, d) => setMidijobber(d.value)} /></Field>
            </QuestionBlock>

            <QuestionBlock question="Wie viele Menschen arbeiten zur gleichen Zeit?">
              <Field label="Anzahl gleichzeitig"><Input className={styles.fullWidth} value={gleichzeitig} onChange={(_, d) => setGleichzeitig(d.value)} /></Field>
            </QuestionBlock>

            <QuestionBlock
              question="Wie hoch ist die Bruttojahreslohn- und Gehaltssumme nach BG-Meldung?"
              hint="NICHT nach der BWA"
            >
              <Field label="In Gänze (€)"><Input className={styles.fullWidth} value={bgGesamt} onChange={(_, d) => setBgGesamt(d.value)} /></Field>
              <Field label="Büro / kfm. Angestellte (€)"><Input className={styles.fullWidth} value={bgBuero} onChange={(_, d) => setBgBuero(d.value)} /></Field>
              <Field label="Gewerbliche / technische Mitarbeiter (€)"><Input className={styles.fullWidth} value={bgGewerblich} onChange={(_, d) => setBgGewerblich(d.value)} /></Field>
              <Field label="Außendienst / Montage / Baustelle (€)"><Input className={styles.fullWidth} value={bgAussendienst} onChange={(_, d) => setBgAussendienst(d.value)} /></Field>
              <Field label="Aushilfen / Minijobber / Praktikanten (€)"><Input className={styles.fullWidth} value={bgAushilfen} onChange={(_, d) => setBgAushilfen(d.value)} /></Field>
              <Field label="Geschäftsführung / Inhaber (€)"><Input className={styles.fullWidth} value={bgGeschaeftsfuehrung} onChange={(_, d) => setBgGeschaeftsfuehrung(d.value)} /></Field>
            </QuestionBlock>

            <QuestionBlock question="Arbeiten Sie mit Leiharbeitern?">
              {renderRadioJaNein(leiharbeiter, setLeiharbeiter)}
              {ifYes(
                leiharbeiter,
                <>
                  <Field label="Mit wie vielen?"><Input className={styles.fullWidth} value={leiharbeiterAnzahl} onChange={(_, d) => setLeiharbeiterAnzahl(d.value)} /></Field>
                  <Field label="Regelmäßig?"><Input className={styles.fullWidth} value={leiharbeiterRegelmaessig} onChange={(_, d) => setLeiharbeiterRegelmaessig(d.value)} /></Field>
                </>
              )}
            </QuestionBlock>

            <QuestionBlock question="Beauftragen Sie Subunternehmer?">
              {renderRadioJaNein(subunternehmer, setSubunternehmer)}
              {ifYes(
                subunternehmer,
                <>
                  <Field label="Wie viele?"><Input className={styles.fullWidth} value={subunternehmerAnzahl} onChange={(_, d) => setSubunternehmerAnzahl(d.value)} /></Field>
                  <Field label="Wie hoch ist der Umsatzanteil?"><Input className={styles.fullWidth} value={subunternehmerUmsatzanteil} onChange={(_, d) => setSubunternehmerUmsatzanteil(d.value)} /></Field>
                </>
              )}
            </QuestionBlock>

            <QuestionBlock question="Beschäftigen Sie Freelancer?">
              {renderRadioJaNein(freelancer, setFreelancer)}
              {ifYes(
                freelancer,
                <>
                  <Field label="Wie viele?"><Input className={styles.fullWidth} value={freelancerAnzahl} onChange={(_, d) => setFreelancerAnzahl(d.value)} /></Field>
                  <Field label="Wie hoch ist der Aufwand?"><Input className={styles.fullWidth} value={freelancerAufwand} onChange={(_, d) => setFreelancerAufwand(d.value)} /></Field>
                </>
              )}
            </QuestionBlock>

            <QuestionBlock
              question="In welchen Bereichen sind die vorgenannten Personen tätig?"
              hint="ca. %-Verteilung auf die Bereiche"
            >
              <Field label="Büro"><Input className={styles.fullWidth} value={bereichBuero} onChange={(_, d) => setBereichBuero(d.value)} /></Field>
              <Field label="Produktion"><Input className={styles.fullWidth} value={bereichProduktion} onChange={(_, d) => setBereichProduktion(d.value)} /></Field>
              <Field label="Planung"><Input className={styles.fullWidth} value={bereichPlanung} onChange={(_, d) => setBereichPlanung(d.value)} /></Field>
              <Field label="Arbeiten auf fremden Grundstücken"><Input className={styles.fullWidth} value={bereichFremdeGrundstuecke} onChange={(_, d) => setBereichFremdeGrundstuecke(d.value)} /></Field>
              <Field label="Montage"><Input className={styles.fullWidth} value={bereichMontage} onChange={(_, d) => setBereichMontage(d.value)} /></Field>
            </QuestionBlock>

            <QuestionBlock question="Entsenden Sie Personen ins Ausland?">
              {renderRadioJaNein(entsendung, setEntsendung)}
              {ifYes(
                entsendung,
                <>
                  <Field label="Welche?"><Input className={styles.fullWidth} value={entsendungWelche} onChange={(_, d) => setEntsendungWelche(d.value)} /></Field>
                  <Field label="Wohin?"><Input className={styles.fullWidth} value={entsendungWohin} onChange={(_, d) => setEntsendungWohin(d.value)} /></Field>
                  <Field label="Wie lange im Durchschnitt?"><Input className={styles.fullWidth} value={entsendungDauer} onChange={(_, d) => setEntsendungDauer(d.value)} /></Field>
                </>
              )}
            </QuestionBlock>

            <QuestionBlock question="Fahren Personen mit dem eigenen PKW?">
              {renderRadioJaNein(eigenerPkw, setEigenerPkw)}
              {ifYes(
                eigenerPkw,
                <>
                  <Field label="Tage im Jahr"><Input className={styles.fullWidth} value={eigenerPkwTage} onChange={(_, d) => setEigenerPkwTage(d.value)} /></Field>
                  <Field label="Oder Kilometer"><Input className={styles.fullWidth} value={eigenerPkwKm} onChange={(_, d) => setEigenerPkwKm(d.value)} /></Field>
                </>
              )}
            </QuestionBlock>
          </div>

          {/* ─────────── Section 10: Benefits & sonstiges ─────────── */}
          <div className={styles.sectionWrap}>
            <h3 className={styles.sectionHeader}>
              <Gift24Regular className={styles.sectionIcon} />
              Besonderes für die Menschen im Unternehmen
            </h3>
            <p className={styles.sectionHint}>
              Tun Sie etwas Besonderes für die Menschen in Ihrem Unternehmen?
            </p>
            {BENEFIT_CATEGORIES.map((c) => {
              const ja = benefitsJaNein[c.key] ?? "";
              return (
                <QuestionBlock key={c.key} question={`Bieten Sie ${c.label} an?`}>
                  <RadioGroup
                    value={ja}
                    onChange={(_, data) =>
                      setBenefitsJaNein((prev) => ({ ...prev, [c.key]: data.value }))
                    }
                    layout="horizontal"
                  >
                    <Radio value="ja" label="Ja" />
                    <Radio value="nein" label="Nein" />
                  </RadioGroup>
                  {ifYes(
                    ja,
                    <Field
                      label="Bitte beschreiben"
                      hint="was, warum, wie viel, etc."
                    >
                      <Textarea
                        className={styles.fullWidth}
                        value={benefits[c.key] ?? ""}
                        onChange={(_, d) => setBenefits((prev) => ({ ...prev, [c.key]: d.value }))}
                        resize="vertical"
                      />
                    </Field>
                  )}
                </QuestionBlock>
              );
            })}
          </div>

          <div className={styles.sectionWrap}>
            <h3 className={styles.sectionHeader}>
              <ChatBubblesQuestion24Regular className={styles.sectionIcon} />
              Sonstiges
            </h3>

            <QuestionBlock question="Informieren Sie Ihre Kunden oder Dritte mit einem Newsletter?">
              {renderRadioJaNein(newsletter, setNewsletter)}
              {ifYes(
                newsletter,
                <Field label="Welcher Newsletter? (bitte später hochladen)">
                  <Input className={styles.fullWidth} value={newsletterDetails} onChange={(_, d) => setNewsletterDetails(d.value)} />
                </Field>
              )}
            </QuestionBlock>

            <QuestionBlock question="Unterliegen Sie einem Tarifvertrag?">
              {renderRadioJaNein(tarifvertragUnterliegt, setTarifvertragUnterliegt)}
              {ifYes(
                tarifvertragUnterliegt,
                <Field label="Welcher Tarifvertrag?">
                  <Input className={styles.fullWidth} value={tarifvertragUnterliegtDetails} onChange={(_, d) => setTarifvertragUnterliegtDetails(d.value)} />
                </Field>
              )}
            </QuestionBlock>

            <QuestionBlock question="Orientieren Sie sich an einem Tarifvertrag?">
              {renderRadioJaNein(tarifvertragOrientiert, setTarifvertragOrientiert)}
              {ifYes(
                tarifvertragOrientiert,
                <Field label="Welcher Tarifvertrag?">
                  <Input className={styles.fullWidth} value={tarifvertragOrientiertDetails} onChange={(_, d) => setTarifvertragOrientiertDetails(d.value)} />
                </Field>
              )}
            </QuestionBlock>

            <QuestionBlock
              question="Welcher IHK oder Handwerkskammer gehören Sie an?"
              hint="Pflicht – abhängig vom Betriebscharakter"
            >
              <Field label="IHK / Handwerkskammer">
                <Input className={styles.fullWidth} value={ihkHwk} onChange={(_, d) => setIhkHwk(d.value)} />
              </Field>
            </QuestionBlock>

            <QuestionBlock question="Sind Sie Mitglied in Berufsverbänden?">
              {renderRadioJaNein(berufsverbaende, setBerufsverbaende)}
              {ifYes(
                berufsverbaende,
                <Field label="Welche Berufsverbände?">
                  <Input className={styles.fullWidth} value={berufsverbaendeDetails} onChange={(_, d) => setBerufsverbaendeDetails(d.value)} />
                </Field>
              )}
            </QuestionBlock>

            <QuestionBlock question="Haben Sie sonstige Mitgliedschaften?">
              {renderRadioJaNein(sonstigeMitgliedschaften, setSonstigeMitgliedschaften)}
              {ifYes(
                sonstigeMitgliedschaften,
                <Field label="Welche Mitgliedschaften?">
                  <Input className={styles.fullWidth} value={sonstigeMitgliedschaftenDetails} onChange={(_, d) => setSonstigeMitgliedschaftenDetails(d.value)} />
                </Field>
              )}
            </QuestionBlock>

            <QuestionBlock
              question="Wie sieht Ihre Erwartungshaltung als möglicher Kunde gegenüber Ihrem Berater aus? Auf was sollte dieser besonders achten?"
            >
              <Field label="Erwartungshaltung">
                <Textarea className={styles.fullWidth} value={erwartung} onChange={(_, d) => setErwartung(d.value)} resize="vertical" />
              </Field>
            </QuestionBlock>

            <QuestionBlock question="Gibt es gewünschte Besonderheiten bei Vertragsgestaltungen, Risiken, Schäden oder Selbstbeteiligungen?">
              <Field label="Besonderheiten">
                <Textarea className={styles.fullWidth} value={besonderheitenVertrag} onChange={(_, d) => setBesonderheitenVertrag(d.value)} resize="vertical" />
              </Field>
            </QuestionBlock>
          </div>

          {/* ─────────── Section 11: Closing ─────────── */}
          <div className={styles.sectionWrap}>
            <h3 className={styles.sectionHeader}>
              <DocumentRibbon24Regular className={styles.sectionIcon} />
              Abschluss
            </h3>

            <QuestionBlock question="Wie zufrieden waren Sie bisher mit Ihrem Makler, Vermittler, Berater und Hauptversicherer?">
              <Field label="Zufriedenheit">
                <Textarea className={styles.fullWidth} value={zufriedenheitMakler} onChange={(_, d) => setZufriedenheitMakler(d.value)} resize="vertical" />
              </Field>
            </QuestionBlock>

            <QuestionBlock question="Was möchten Sie uns sonst noch mit auf den Weg geben?">
              <Field label="Hinweise">
                <Textarea className={styles.fullWidth} value={zusaetzlicheHinweise} onChange={(_, d) => setZusaetzlicheHinweise(d.value)} resize="vertical" />
              </Field>
            </QuestionBlock>

            <QuestionBlock
              question="Welche Unterlagen können Sie nachreichen?"
              hint="Bitte ankreuzen — wir bitten diese später separat um Hochladen."
            >
              <div className={styles.twoCol}>
                <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <input type="checkbox" checked={unterlagenOrganigramm} onChange={(e) => setUnterlagenOrganigramm(e.target.checked)} />
                  Organigramm
                </label>
                <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <input type="checkbox" checked={unterlagenBilanz} onChange={(e) => setUnterlagenBilanz(e.target.checked)} />
                  Bilanz
                </label>
                <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <input type="checkbox" checked={unterlagenAgb} onChange={(e) => setUnterlagenAgb(e.target.checked)} />
                  AGB / Auftragsbestätigungen / Verträge mit Kunden
                </label>
                <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <input type="checkbox" checked={unterlagenBrandschutz} onChange={(e) => setUnterlagenBrandschutz(e.target.checked)} />
                  Brandschutzbericht
                </label>
              </div>
            </QuestionBlock>

            <QuestionBlock question="Welche Genehmigungen liegen vor?">
              <Field label="Genehmigungen von …">
                <Input className={styles.fullWidth} value={unterlagenGenehmigungen} onChange={(_, d) => setUnterlagenGenehmigungen(d.value)} />
              </Field>
            </QuestionBlock>

            <QuestionBlock question="Welche sonstigen Unterlagen können Sie nachreichen?">
              <Field label="Sonstige Unterlagen">
                <Input className={styles.fullWidth} value={unterlagenSonstige} onChange={(_, d) => setUnterlagenSonstige(d.value)} />
              </Field>
            </QuestionBlock>
          </div>

          <div className={styles.actions}>
            <Button
              className={styles.submitButton}
              appearance="primary"
              onClick={handleSubmit}
              disabled={submitting}
            >
              {submitting ? "Wird übermittelt…" : "Risikoanalyse absenden"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RisikoanalyseFormView;
